import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import '../../styles/focus.css';
import '../../styles/dynamic-tab-placeholders.css';
import { ENTITY_NAMES, FAQ_SUFFIX, FIELD_NAMES, FIELD_PATTERNS, AID_CALCULATION_OPTIONS, ATTRIBUTE_NAMES } from '../../constants';
import { Control } from '../../type';
import WhatYouShouldKnow from "../WhatYouShouldKnow";
import Header from "./Header";
import { Footer } from "./Footer";
import EstimatedPrice from "./EstimatedPrice";
import { useTenant } from "../TenantContext";
import EntityAttributesSection from "./EntityAttributesSection";
import { StudentIcon, AcademicIcon, FamilyIcon, ParentFinancesIcon, StudentFinancesIcon, SummaryIcon } from "./TabIcons";
import { validateForm, isFormSectionValid } from "../../utils/formValidation";
import { toTabKey } from "../../utils/toTabKey";
import { tabStateStorage } from "../../utils/tabStateStorage";

// Helper to map a raw API attribute object to a Control shape
const mapApiAttributeToControl = (a: any, screenName: string): Control => ({
    _id: a._id,
    controlId: a._id,
    controlName: a.name,
    name: a.name,
    displayName: a.friendlyName ?? a.name,
    dataType: a.dataType ?? 'String',
    sequence: typeof a.order === 'number' ? a.order : 0,
    visible: true,
    onclauseEnable: false,
    required: !!a.isRequired,
    screenName,
    tenant: '',
    description: a.description ?? '',
    isRequired: !!a.isRequired,
    isMultivalued: !!a.isMultivalued,
    enablingAttribute: a.enablingAttribute,
    enablingValue: a.enablingValue,
    possibleValues: a.possibleValues,
    possibleValuesSource: a.possibleValuesSource,
    possibleMinValue: a.possibleMinValue,
    possibleMaxValue: a.possibleMaxValue,
    possibleRangeStep: a.possibleRangeStep,
    controltype: (() => {
        if (a.isInformational) return 'label';
        return a.possibleValues ? 'dropdown' : 'textinput';
    })() as any,
});

// Helper function to build the payload for calculate-derived-attributes API
const buildCalculatePayload = (
    estimateBody: any,
    formValues: Record<string, string>,
    tabAttributes: Record<string, Control[]>
): any => {
    if (!estimateBody?.objects) {
        return estimateBody;
    }

    // Clone the estimate body to avoid mutation
    const payload = structuredClone(estimateBody);

    // Update all objects with form values
    payload.objects = payload.objects.map((obj: any) => {
        const entityName = obj.name;
        const entityAttributes = tabAttributes[entityName] || [];

        // Update attributes with form values
        const updatedAttributes = obj.attributes.map((attr: any) => {
            // Find the control for this attribute
            const control = entityAttributes.find(
                (c: Control) => c._id === attr._id || c.name === attr.name
            );

            if (!control) return attr;

            // Get the form value for this attribute
            const formValue = formValues[control._id];

            // Handle Boolean dataType attributes specifically
            if (control.dataType === 'Boolean') {
                // Determine if checkbox is checked based on form value
                const isChecked = formValue === 'True' || formValue === 'true' ||
                    formValue === 'Yes' || formValue === 'yes' ||
                    formValue === 'TRUE';

                // Set API value: "True" if checked, "False" if unchecked
                const apiValue = isChecked ? 'True' : 'False';

                // Set display value: "Yes" if checked, "No" if unchecked
                const displayValue = isChecked ? 'Yes' : 'No';


                return {
                    ...attr,
                    value: apiValue,
                    displayValue: displayValue,
                    isNa: false
                };
            }

            // Handle String dataType with possibleValues (static dropdowns) or possibleValuesSource (dynamic dropdowns)
            if (control.dataType === 'String' && formValue) {
                let displayValue: string = formValue; // Default to value itself
                let foundDisplayValue = false;

                // Check for static possibleValues first
                const possibleVals = control.possibleValues;
                if (Array.isArray(possibleVals) && possibleVals.length > 0) {
                    // Search through possibleValues for matching value
                    for (const option of possibleVals) {
                        let optionMatches = false;
                        let optionDisplayValue: string | null = null;

                        if (typeof option === 'object' && option !== null) {
                            const opt = option as Record<string, any>;
                            if (String(opt.value) === String(formValue)) {
                                optionMatches = true;
                                optionDisplayValue = opt.displayValue;
                            }
                        } else if (String(option) === String(formValue)) {
                            optionMatches = true;
                        }

                        if (optionMatches && optionDisplayValue) {
                            displayValue = optionDisplayValue;
                            foundDisplayValue = true;
                            break;
                        }
                    }
                }
                // Special handling for High School field with possibleValuesSource (dynamic API)
                else if (control.possibleValuesSource) {
                    const isHighSchoolField = control.name?.toLowerCase().includes('high school') ||
                        control.name?.toLowerCase().includes('school');

                    if (isHighSchoolField) {
                        // Try to get school name from localStorage
                        try {
                            const storedData = localStorage.getItem('npc_high_school');
                            if (storedData) {
                                const parsed = JSON.parse(storedData);
                                // Validate that the stored CEEB matches the current form value
                                if (parsed?.data?.ceeb && String(parsed.data.ceeb) === String(formValue)) {
                                    displayValue = parsed.data.hsName ?? formValue;
                                    foundDisplayValue = true;
                                }
                            }
                        } catch (error) {
                            console.warn('Failed to read high school from localStorage:', error);
                        }
                    }
                }

                // Only set displayValue if we found a different value than the raw value
                if (foundDisplayValue && displayValue !== formValue) {
                    return {
                        ...attr,
                        value: formValue,
                        displayValue: displayValue,
                        isNa: false
                    };
                }
            }

            // Handle empty string (Select option) by setting to null
            if (formValue === '') {
                return {
                    ...attr,
                    value: null,
                    displayValue: null,
                    isNa: true
                };
            }

            // Only update if we have a form value for other types
            if (formValue !== undefined && formValue !== null) {
                return {
                    ...attr,
                    value: formValue,
                    isNa: false
                };
            }

            // If no form value but attribute has a default value, use that
            if (control.defaultValue !== undefined && control.defaultValue !== null && control.defaultValue !== '') {
                return {
                    ...attr,
                    value: control.defaultValue,
                    isNa: false
                };
            }

            return attr;
        });

        // Post-process: null out value and constantValue for LOOKUP-computed
        // attributes so the backend recomputes them fresh from decision tables
        // instead of reusing stale calculated values from the previous response.
        const clearedAttributes = updatedAttributes.map((attr: any) => {
            if (attr.operator === 'LOOKUP') {
                return { ...attr, value: null, constantValue: null, isNa: true };
            }
            return attr;
        });

        return {
            ...obj,
            attributes: clearedAttributes
        };
    });

    return payload;
};

interface DynamicTabProps {
    tabs: string[];
    entitiesData: Control[];
    updateStudentRequiredControls: (controls: Control[]) => void;
    setFormValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    formValues: Record<string, string>;
    errors: Record<string, string>;
    controls: Control[];
    onStartCalculator: () => void; // Add this prop
}

const TAB_ENTITY_MAP: Record<string, string[]> = {
    [toTabKey(ENTITY_NAMES.STUDENT_INFORMATION)]: [
        ENTITY_NAMES.STUDENT_INFORMATION,
        ENTITY_NAMES.STUDENT_CONTACT
    ],
    [toTabKey(ENTITY_NAMES.ACADEMIC_SCORES)]: [ENTITY_NAMES.ACADEMIC_SCORES],
    [toTabKey(ENTITY_NAMES.FAMILY_INFORMATION)]: [ENTITY_NAMES.FAMILY_INFORMATION],
    [toTabKey(ENTITY_NAMES.PARENT_FINANCES)]: [ENTITY_NAMES.PARENT_FINANCES],
    [toTabKey(ENTITY_NAMES.STUDENT_FINANCES)]: [ENTITY_NAMES.STUDENT_FINANCES],
    [toTabKey(ENTITY_NAMES.SUMMARY)]: [ENTITY_NAMES.SUMMARY],
};

const TAB_CONFIG: Record<string, { icon: React.FC<{ isActive: boolean; isFilled?: boolean; color?: string; inactiveColor?: string; }>; ariaLabel: string }> = {
    [toTabKey(ENTITY_NAMES.STUDENT_INFORMATION)]: { icon: StudentIcon, ariaLabel: formatLabel(ENTITY_NAMES.STUDENT_INFORMATION) },
    [toTabKey(ENTITY_NAMES.ACADEMIC_SCORES)]: { icon: AcademicIcon, ariaLabel: formatLabel(ENTITY_NAMES.ACADEMIC_SCORES) },
    [toTabKey(ENTITY_NAMES.FAMILY_INFORMATION)]: { icon: FamilyIcon, ariaLabel: formatLabel(ENTITY_NAMES.FAMILY_INFORMATION) },
    [toTabKey(ENTITY_NAMES.PARENT_FINANCES)]: { icon: ParentFinancesIcon, ariaLabel: formatLabel(ENTITY_NAMES.PARENT_FINANCES) },
    [toTabKey(ENTITY_NAMES.STUDENT_FINANCES)]: { icon: StudentFinancesIcon, ariaLabel: formatLabel(ENTITY_NAMES.STUDENT_FINANCES) },
    [toTabKey(ENTITY_NAMES.SUMMARY)]: { icon: SummaryIcon, ariaLabel: formatLabel(ENTITY_NAMES.SUMMARY) },
};

function formatLabel(entityName: string): string {
    if (!entityName) {
        return "";
    }
    // Converts PascalCase or camelCase to Title Case, e.g., "StudentInformation" -> "Student Information"
    return entityName
        .replaceAll(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
}

// Helper function to check if user should skip to Summary
function shouldSkipToSummary(familyAttributes: Control[], formValues: Record<string, string>): boolean {
    // First check for aid calculation method
    const aidCalculationAttribute = familyAttributes.find(attr =>
        attr.name.toLowerCase().includes('aid calculation') ||
        attr.name.toLowerCase().includes('calculation method')
    );

    if (aidCalculationAttribute) {
        const selectedOption = formValues[aidCalculationAttribute._id];

        // If "Help me calculate" selected, DON'T skip (so return false)
        if (selectedOption === AID_CALCULATION_OPTIONS.NEED_BASED) {
            return false;
        }

        // If SAI option selected and SAI value provided, skip to Summary
        if (selectedOption === AID_CALCULATION_OPTIONS.SAI) {
            const saiAttribute = familyAttributes.find(attr =>
                attr.name === FIELD_NAMES.SAI ||
                attr.name.toLowerCase().includes('sai') ||
                attr.name.toLowerCase().includes('student aid')
            );
            return !!(saiAttribute && formValues[saiAttribute._id]?.trim());
        }

        // If skip options selected, skip to Summary
        if (selectedOption === AID_CALCULATION_OPTIONS.SKIP_INTERNATIONAL ||
            selectedOption === AID_CALCULATION_OPTIONS.SKIP_NO_NEED) {
            return true;
        }
    }

    // Fallback: check for SAI without aid calculation method (legacy behavior)
    const saiAttribute = familyAttributes.find(attr =>
        attr.name === FIELD_NAMES.SAI ||
        attr.name.toLowerCase().includes('sai') ||
        attr.name.toLowerCase().includes('student aid')
    );
    if (saiAttribute && formValues[saiAttribute._id]?.trim()) {
        return true;
    }

    // Fallback: check for skip financial aid options
    const skipFinancialAidAttribute = familyAttributes.find(attr =>
        FIELD_PATTERNS.SKIP_FINANCIAL_AID.some(pattern =>
            attr.name.toLowerCase().includes(pattern)
        )
    );
    if (skipFinancialAidAttribute) {
        const value = formValues[skipFinancialAidAttribute._id];
        if (skipFinancialAidAttribute.dataType === 'Boolean') {
            return value === 'true';
        } else {
            return !!value?.trim();
        }
    }

    return false;
}

export default function DynamicTab({
    tabs,
    entitiesData,
    updateStudentRequiredControls,
    setFormValues,
    formValues,
    errors,
    controls,
    onStartCalculator,
}: Readonly<DynamicTabProps>) {
    const [activeTab, setActiveTab] = useState(tabs[0]);
    const {
        tenantConfig,
        estimateBody,
        getWelcomeFAQAttributes,
        getEntityIdByName,
        calculateDerivedAttributesForEntity,
        derivedAttributesData: contextDerivedData,
        estimateId
    } = useTenant();

    const activeIndex = tabs.indexOf(activeTab);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [showErrors, setShowErrors] = useState(false);
    const [completedTabs, setCompletedTabs] = useState<Set<string>>(new Set());
    const [tabAttributes, setTabAttributes] = useState<Record<string, Control[]>>({});
    const [shouldReturnToSummary, setShouldReturnToSummary] = useState(false);

    // Extract disclaimerText from Summary attributes for EmailResultsModal
    const disclaimerText = React.useMemo(() => {
        const summaryAttrs = tabAttributes[ENTITY_NAMES.SUMMARY] || [];
        if (summaryAttrs.length > 0) {
            // Find the attribute with empty name and a long description
            const disclaimerAttr = summaryAttrs.find(
                attr => attr.name === '' && attr.description && attr.description.length > 100
            );
            if (disclaimerAttr?.description) {
                // Replace both escaped \n and actual newlines with <br> for proper line breaks
                return disclaimerAttr.description.replace(/\\n/g, '<br>').replace(/\n/g, '<br>');
            }
        }
        return '';
    }, [tabAttributes]);

    // Extract noteText from Summary attributes for EmailResultsModal
    const noteText = React.useMemo(() => {
        // Get Summary attributes from derivedAttributesData (which has Attribute objects with order, value properties)
        const summaryObject = contextDerivedData?.objects?.find((obj: any) => obj.name === ENTITY_NAMES.SUMMARY);
        const summaryAttributes = summaryObject?.attributes || [];

        if (summaryAttributes.length > 0) {
            // Find the first Summary attribute with order=1 that has description text
            const firstOrderAttr = summaryAttributes.find(
                (attr: any) => attr.order === 1 && attr.description && attr.description.trim().length > 0
            );
            if (firstOrderAttr) {
                const text = firstOrderAttr.description ?? firstOrderAttr.constantValue ?? firstOrderAttr.defaultValue ?? firstOrderAttr.value;
                if (text?.trim()) {
                    return text;
                }
            }
        }
        return '';
    }, [contextDerivedData]);

    // Track if we've already loaded student info for this session
    const loadedTabsRef = useRef<Set<string>>(new Set());
    const [loadingNextTab, setLoadingNextTab] = useState(false);

    const [estimateCalculated, setEstimateCalculated] = useState(false);
    const [financesWereSkipped, setFinancesWereSkipped] = useState(false);
    const [currentEntityId, setCurrentEntityId] = useState<string | undefined>(undefined);
    // Track specific navigation flow: skip finances from family info -> return to family info -> select "No" for independent
    // const [hasSkippedFinancesFromFamilyInfo, setHasSkippedFinancesFromFamilyInfo] = useState(false);
    // const [hasReturnedToFamilyInfo, setHasReturnedToFamilyInfo] = useState(false);

    // Track finances skip status

    const backgroundColor = tenantConfig?.authority?.authorityBranding?.primaryColor;

    // Load tab states from localStorage on mount
    useEffect(() => {
        const savedStates = tabStateStorage.getTabStates();
        if (savedStates) {
            // Load finances skipped status
            const wasSkipped = tabStateStorage.getFinancesSkipped();
            setFinancesWereSkipped(wasSkipped);
        }
    }, []);

    // Track current entity ID when active tab changes
    useEffect(() => {
        const entityNames = TAB_ENTITY_MAP[activeTab] || [];
        const primaryEntityName = entityNames[0];
        if (primaryEntityName) {
            const entityId = getEntityIdByName(primaryEntityName);
            setCurrentEntityId(entityId);
        }

        // Scroll to top when a new page is loaded
        window.scrollTo({ top: 0, behavior: 'smooth' });

        // Track when user returns to Family Information after skipping finances
        if (activeTab === toTabKey(ENTITY_NAMES.FAMILY_INFORMATION) && financesWereSkipped) {
        }
    }, [activeTab, getEntityIdByName, financesWereSkipped]);

    // Update tab disabled states when form values change
    useEffect(() => {
        const familyAttributes = tabAttributes[ENTITY_NAMES.FAMILY_INFORMATION] || [];

        // Find independent student control
        const independentAttribute = familyAttributes.find(attr =>
            attr.name?.toLowerCase().includes('independent student') ||
            attr.name?.toLowerCase().includes('are you an independent student')
        );

        // Track independent student status changes
        if (independentAttribute) {



            const isIndependent = formValues[independentAttribute._id] === 'Yes';
            const isNotIndependent = formValues[independentAttribute._id] === 'No';

            // Store in localStorage
            const parentFinancesTab = toTabKey(ENTITY_NAMES.PARENT_FINANCES);
            const studentFinancesTab = toTabKey(ENTITY_NAMES.STUDENT_FINANCES);

            tabStateStorage.setTabState(parentFinancesTab, {
                isDisabled: isIndependent,
                isCompleted: isIndependent, // Mark as completed if disabled
            });

            // If switching to independent, mark parent finances as completed
            // so it doesn't block navigation
            if (isIndependent) {
                setCompletedTabs(prev => {
                    const newSet = new Set(prev);
                    newSet.add(parentFinancesTab);
                    return newSet;
                });
            } else {
                // If switching back to dependent, remove from completed if not actually filled
                setCompletedTabs(prev => {
                    const newSet = new Set(prev);

                    // Check if parent finances has actual data
                    const parentAttrs = tabAttributes[ENTITY_NAMES.PARENT_FINANCES] || [];
                    const hasParentData = parentAttrs.some(attr =>
                        formValues[attr._id] && formValues[attr._id] !== ''
                    );

                    if (!hasParentData) {
                        newSet.delete(parentFinancesTab);
                    }

                    return newSet;
                });
            }

            // CRITICAL: When independent status changes to 'No', clear loaded status for both finance tabs
            // This ensures they will be reloaded with correct context when clicked
            if (isNotIndependent) {
                loadedTabsRef.current.delete(parentFinancesTab);
                loadedTabsRef.current.delete(studentFinancesTab);

                // Also clear Student Finances from completedTabs to force reload
                setCompletedTabs(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(studentFinancesTab);
                    return newSet;
                });
            }
        }
    }, [formValues, tabAttributes, setCompletedTabs]);

    // Extract estimated net price from derived attributes data
    const estimatedNetPrice = useMemo(() => {
        if (!contextDerivedData?.objects) return undefined;

        const allAttributes = contextDerivedData.objects.flatMap(obj => obj.attributes);
        const priceAttr = allAttributes.find(attr =>
            attr.name === 'Estimated Net Price' ||
            attr.name?.toLowerCase().includes('estimated net price')
        );

        if (priceAttr?.value !== null && priceAttr?.value !== undefined) {
            return String(priceAttr.value);
        }

        return undefined;
    }, [contextDerivedData]);

    // Handle navigation from Summary accordion Edit buttons
    const handleNavigateToSection = useCallback(async (sectionKey: string) => {
        // Map section keys to tab keys
        const sectionToTabMap: Record<string, string> = {
            'student': toTabKey(ENTITY_NAMES.STUDENT_INFORMATION),
            'academic': toTabKey(ENTITY_NAMES.ACADEMIC_SCORES),
            'family': toTabKey(ENTITY_NAMES.FAMILY_INFORMATION),
            'parent': toTabKey(ENTITY_NAMES.PARENT_FINANCES),
            'studentFinances': toTabKey(ENTITY_NAMES.STUDENT_FINANCES),
        };

        const targetTab = sectionToTabMap[sectionKey];
        if (targetTab && tabs.includes(targetTab)) {
            // Track when user navigates from Summary back to Family Information
            if (activeTab === toTabKey(ENTITY_NAMES.SUMMARY) && targetTab === toTabKey(ENTITY_NAMES.FAMILY_INFORMATION) && financesWereSkipped) {
            }

            // CRITICAL: Call calculate-derived-attributes API before navigating to finance tabs from Summary
            // This ensures the tab is properly enabled when clicking Edit from Summary
            const isNavigatingToFinances = targetTab === toTabKey(ENTITY_NAMES.STUDENT_FINANCES) ||
                targetTab === toTabKey(ENTITY_NAMES.PARENT_FINANCES);

            if (isNavigatingToFinances) {
                const targetEntityNames = TAB_ENTITY_MAP[targetTab] || [];
                if (targetEntityNames.length > 0) {
                    const targetEntityId = getEntityIdByName(targetEntityNames[0]);
                    if (targetEntityId) {
                        setLoadingNextTab(true);
                        try {
                            // Build payload with current form values
                            const currentPayload = buildCalculatePayload(
                                contextDerivedData || estimateBody,
                                formValues,
                                tabAttributes
                            );

                            // Get context entity ID (Family Information when navigating from Summary)
                            const familyEntityId = getEntityIdByName(ENTITY_NAMES.FAMILY_INFORMATION);

                            // Call calculate-derived-attributes API
                            const response = await calculateDerivedAttributesForEntity(
                                targetEntityId,
                                currentPayload,
                                familyEntityId
                            );

                            // Update tab attributes with response data
                            if (response?.objects) {
                                const entityObject = response.objects.find((o: any) =>
                                    o.name === targetEntityNames[0]
                                );

                                if (entityObject?.attributes) {
                                    const controls = entityObject.attributes.map((a: any) => mapApiAttributeToControl(a, targetEntityNames[0]));

                                    handleAttributesFetched(controls, targetEntityNames[0]);
                                    loadedTabsRef.current.add(targetTab);
                                }
                            }
                        } catch (error) {
                            console.error(`Failed to load data for ${targetTab} when editing from Summary:`, error);
                        } finally {
                            setLoadingNextTab(false);
                        }
                    }
                }
            }

            // If navigating to Summary, mark it completed so UI reflects skipped tabs
            if (targetTab === toTabKey(ENTITY_NAMES.SUMMARY)) {
                setCompletedTabs(prev => new Set(prev).add(targetTab));
            }
            setShouldReturnToSummary(true);
            setActiveTab(targetTab);
            // Scroll to top for better UX
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabs, activeTab, financesWereSkipped, setCompletedTabs, setShouldReturnToSummary, setActiveTab, getEntityIdByName, calculateDerivedAttributesForEntity, contextDerivedData, estimateBody, formValues, tabAttributes, setLoadingNextTab]);
    // Note: handleAttributesFetched is intentionally omitted from deps as it's defined later and is a stable useCallback

    // Collect all attributes from all entities for global validation
    const allAttributes = useMemo(() => {
        return Object.values(tabAttributes).flat();
    }, [tabAttributes]);

    // Global form validation state (removed - using the one on line 822 instead)

    const handleAttributesFetched = useCallback((fetchedControls: Control[], entityName: string) => {
        updateStudentRequiredControls(fetchedControls);

        setTabAttributes(prev => {
            const newTabAttributes = { ...prev };
            // Deduplicate by both _id and name to prevent duplicates when the same control
            // is loaded from two sources (e.g., applicableEntities vs. calculate-derived-attributes
            // response) that may assign different _id values to the same logical attribute.
            const existingControls = newTabAttributes[entityName] || [];
            const existingIds = new Set(existingControls.map(ec => ec._id));
            const existingNames = new Set(
                existingControls.map(ec => (ec.name ?? '').toLowerCase()).filter(Boolean)
            );
            const newControls = fetchedControls.filter(
                fc => !existingIds.has(fc._id) && !existingNames.has((fc.name ?? '').toLowerCase())
            );
            if (newControls.length > 0) {
                newTabAttributes[entityName] = [...existingControls, ...newControls];
            }
            return newTabAttributes;
        });

        // Set default values and displayValue for newly fetched controls
        for (const control of fetchedControls) {
            setFormValues(prev => {
                // Skip if value already exists (avoid overwriting user input)
                if (prev[control._id] !== undefined) {
                    return prev;
                }

                // For Boolean fields, use displayValue if available, otherwise use value
                if (control.dataType === 'Boolean') {
                    const displayValue = (control as any).displayValue;
                    const value = (control as any).value;

                    // If displayValue exists, use it to determine the form value
                    if (displayValue !== undefined && displayValue !== null) {
                        const isYes = displayValue === 'Yes' || displayValue === 'yes';
                        return { ...prev, [control._id]: isYes ? 'True' : 'False' };
                    }

                    // Otherwise use the value field
                    if (value !== undefined && value !== null) {
                        const isTrue = value === 'True' || value === 'true' || value === true;
                        return { ...prev, [control._id]: isTrue ? 'True' : 'False' };
                    }

                    // Default to False for Boolean fields
                    return { ...prev, [control._id]: 'False' };
                }

                // For non-Boolean fields, use defaultValue if available
                if (control.defaultValue !== undefined && control.defaultValue !== null) {
                    return { ...prev, [control._id]: control.defaultValue };
                }

                return prev;
            });
        }
    }, [updateStudentRequiredControls, setFormValues, setTabAttributes]);

    // Effect to apply defaultValue when conditionally-enabled fields become visible
    useEffect(() => {
        const allControls = Object.values(tabAttributes).flat();

        // Find controls that have both enablingAttribute and defaultValue
        const conditionalControlsWithDefaults = allControls.filter(
            control => control.enablingAttribute &&
                control.defaultValue !== undefined &&
                control.defaultValue !== null &&
                control.defaultValue !== ''
        );

        if (conditionalControlsWithDefaults.length === 0) return;

        // Get independent student status to check if parent fields should be disabled
        const familyAttributes = tabAttributes[ENTITY_NAMES.FAMILY_INFORMATION] || [];
        const independentAttribute = familyAttributes.find(attr =>
            attr.name?.toLowerCase().includes('independent student')
        );
        const independentValue = independentAttribute ? formValues[independentAttribute._id] : undefined;
        const isIndependentStudent = independentValue === 'Yes';

        // Helper to dynamically identify parent-related fields
        const isParentRelatedField = (control: Control): boolean => {
            if (!control) return false;

            const indicators = [
                control.name,
                control.friendlyName,
                control.description,
                control.toolTip
            ].filter(Boolean).map(s => String(s).toLowerCase());

            // Check if any indicator contains "parent"
            return indicators.some(text => text.includes('parent'));
        };

        setFormValues(prev => {
            let updated = { ...prev };
            let hasChanges = false;

            for (const control of conditionalControlsWithDefaults) {
                // Skip if this control already has a value
                if (updated[control._id] !== undefined && updated[control._id] !== null && updated[control._id] !== '') {
                    continue;
                }

                // Skip parent fields if student is independent
                if (isIndependentStudent && isParentRelatedField(control)) {
                    continue;
                }

                // Check if the enabling condition is met using the evaluateEnablingCondition logic
                const { enablingAttribute, enablingValue } = control;

                if (!enablingAttribute) continue;

                const enablingControl = allControls.find(
                    attr => attr.name?.trim().toLowerCase() === enablingAttribute?.trim().toLowerCase()
                );

                if (!enablingControl) continue;

                const enablingControlValue = updated[enablingControl._id];

                // Only apply default if the enabling condition is met
                if (enablingControlValue !== undefined && enablingControlValue !== null && enablingControlValue !== '') {
                    // Check if values match (handle both exact match and trimmed match)
                    const actualValue = String(enablingControlValue).trim();
                    const expectedValue = String(enablingValue ?? '').trim().replaceAll(String.raw`\n`, '').replaceAll('\n', '');

                    const isEnabled = actualValue === expectedValue ||
                        (enablingControl.isMultivalued && expectedValue.startsWith(actualValue));

                    if (isEnabled && control.defaultValue) {
                        updated[control._id] = control.defaultValue;
                        hasChanges = true;
                    }
                }
            }

            return hasChanges ? updated : prev;
        });
    }, [tabAttributes, formValues, setFormValues]);

    // Helper to ensure ParentFinances and StudentFinances are available even when backend doesn't return them
    const ensureFinanceEntitiesLoaded = useCallback(() => {
        if (!estimateBody) {

            return;
        }

        // Access applicableEntities from estimateBody (backend response structure)
        const applicableEntities = (estimateBody as any).applicableEntities ?? [];


        // Find Parent Finances and Student Finances entities from applicableEntities
        const parentFinancesEntity = applicableEntities.find(
            (entity: any) => entity.name?.toLowerCase() === ENTITY_NAMES.PARENT_FINANCES.toLowerCase()
        );
        const studentFinancesEntity = applicableEntities.find(
            (entity: any) => entity.name?.toLowerCase() === ENTITY_NAMES.STUDENT_FINANCES.toLowerCase()
        );

        // Check if these entities exist in estimateBody.objects
        const hasParentFinancesInEstimate = estimateBody?.objects?.some(
            (obj) => obj.name?.toLowerCase() === ENTITY_NAMES.PARENT_FINANCES.toLowerCase()
        );
        const hasStudentFinancesInEstimate = estimateBody?.objects?.some(
            (obj) => obj.name?.toLowerCase() === ENTITY_NAMES.STUDENT_FINANCES.toLowerCase()
        );

        // CRITICAL FIX: Check if entities are already loaded in tabAttributes to prevent duplicates
        const hasParentFinancesInTabAttributes = tabAttributes[ENTITY_NAMES.PARENT_FINANCES]?.length > 0;
        const hasStudentFinancesInTabAttributes = tabAttributes[ENTITY_NAMES.STUDENT_FINANCES]?.length > 0;


        // Load Parent Finances into tabAttributes if missing AND not already in tabAttributes
        if (!hasParentFinancesInEstimate && !hasParentFinancesInTabAttributes && parentFinancesEntity?.attributes && Array.isArray(parentFinancesEntity.attributes)) {
            const controls = parentFinancesEntity.attributes.map((a: any) => mapApiAttributeToControl(a, ENTITY_NAMES.PARENT_FINANCES));
            handleAttributesFetched(controls, ENTITY_NAMES.PARENT_FINANCES);
        }

        // Load Student Finances into tabAttributes if missing AND not already in tabAttributes
        if (!hasStudentFinancesInEstimate && !hasStudentFinancesInTabAttributes && studentFinancesEntity?.attributes && Array.isArray(studentFinancesEntity.attributes)) {
            const controls = studentFinancesEntity.attributes.map((a: any) => mapApiAttributeToControl(a, ENTITY_NAMES.STUDENT_FINANCES));
            handleAttributesFetched(controls, ENTITY_NAMES.STUDENT_FINANCES);
        }
    }, [estimateBody, handleAttributesFetched, tabAttributes]);

    const handleValueChange = useCallback((controlId: string, value: any) => {
        setFormValues(prev => ({ ...prev, [controlId]: value }));

        // Check if this is the Independent Student field and call API if changed to "No"
        const allAttrs = Object.values(tabAttributes).flat();
        const independentStudentControl = allAttrs.find(attr =>
            attr.name?.toLowerCase().includes('independent student') ||
            attr.name?.toLowerCase().includes('are you an independent student')
        );

        if (independentStudentControl && controlId === independentStudentControl._id) {
            // If changed to "No", ensure finance entities are loaded
            if (value === 'No') {
                // Load finance entities from applicableEntities
                setTimeout(() => {
                    ensureFinanceEntitiesLoaded();
                }, 100);
            }
        }

        if (showErrors) { // Only clear validation errors if they are being shown
            const newErrors = { ...validationErrors };
            if (newErrors[controlId]) {
                delete newErrors[controlId];
            }

            // Special handling: If clearing GPA field, also clear GPA scale error
            const userGPAControl = allAttrs.find(
                (attr) =>
                    attr.name?.toLowerCase().includes("your high school gpa") ||
                    attr.name?.toLowerCase().includes("high school gpa") ||
                    (attr as any).friendlyName?.toLowerCase().includes("userinputgpa")
            );

            const gpaScaleControl = allAttrs.find(
                (attr) =>
                    attr.name?.toLowerCase().includes("gpa out of") ??
                    (attr as any).friendlyName?.toLowerCase().includes("gpaoutof") ??
                    attr.name?.toLowerCase().includes("gpa scale")
            );

            // If user is clearing the GPA field, remove GPA scale error
            if (userGPAControl && gpaScaleControl && controlId === userGPAControl._id) {
                const gpaValue = String(value ?? "").trim();
                if (gpaValue === "") {
                    // GPA field is being cleared, remove GPA scale error
                    if (newErrors[gpaScaleControl._id]) {
                        delete newErrors[gpaScaleControl._id];
                    }
                }
            }

            setValidationErrors(newErrors);
        }
    }, [showErrors, validationErrors, tabAttributes, setFormValues, ensureFinanceEntitiesLoaded]);

    // This effect replaces the need for a separate handleStartCalculator function
    // It triggers when the component mounts or when the user is sent to the Student Info tab
    useEffect(() => {
        const studentTabKey = toTabKey(ENTITY_NAMES.STUDENT_INFORMATION);

        // Only run if the active tab is Student Information and it hasn't been loaded yet
        if (activeTab !== studentTabKey || loadedTabsRef.current.has(studentTabKey) || loadingNextTab) {
            return;
        }

        const loadStudentInfo = async () => {
            setLoadingNextTab(true);
            try {
                // Load StudentInformation attributes from initial estimateBody without API call
                const studentObject = estimateBody?.objects?.find((o: any) => o.name === ENTITY_NAMES.STUDENT_INFORMATION);
                if (studentObject?.attributes) {
                    const controls = studentObject.attributes.map((a: any) => mapApiAttributeToControl(a, ENTITY_NAMES.STUDENT_INFORMATION));
                    handleAttributesFetched(controls, ENTITY_NAMES.STUDENT_INFORMATION);
                }

                loadedTabsRef.current.add(studentTabKey); // Mark as loaded
            } catch (error) {
                console.error('Failed to load Student Information data:', error);
                setValidationErrors(prev => ({
                    ...prev,
                    studentInfoLoad: 'Failed to load student information. Please refresh and try again.'
                }));
            } finally {
                setLoadingNextTab(false);
            }
        };

        loadStudentInfo();

    }, [activeTab, estimateBody, loadingNextTab, handleAttributesFetched]);

    // Ensure ParentFinances and StudentFinances are loaded even when backend doesn't return them
    useEffect(() => {
        ensureFinanceEntitiesLoaded();
    }, [ensureFinanceEntitiesLoaded]);

    // When navigating to Summary, ensure finance entities are loaded if student is dependent
    useEffect(() => {
        const isSummaryTab = activeTab === toTabKey(ENTITY_NAMES.SUMMARY);
        if (!isSummaryTab) {
            return;
        }

        // Check if student is dependent (Independent Student = "No")
        const allAttrs = Object.values(tabAttributes).flat();
        const independentStudentAttr = allAttrs.find(attr =>
            attr.name?.toLowerCase().includes('independent student') ||
            attr.name?.toLowerCase().includes('are you an independent student')
        );

        if (!independentStudentAttr) {
            return;
        }

        const independentStudentValue = formValues[independentStudentAttr._id];
        const isDependent = independentStudentValue === 'No';

        if (isDependent) {
            ensureFinanceEntitiesLoaded();
        }
    }, [activeTab, formValues, tabAttributes, ensureFinanceEntitiesLoaded]);

    // Helper: Get all attributes for a specific tab
    const getAttributesForTab = useCallback((tabId: string): Control[] => {
        const entityNames = TAB_ENTITY_MAP[tabId] || [];
        return entityNames.flatMap(entity => tabAttributes[entity] || []);
    }, [tabAttributes]);

    // Helper: Validate all required fields in the current tab
    // Note: This uses isFormSectionValid which is already imported and doesn't need evaluateEnablingCondition
    const validateCurrentTab = useCallback((tabId: string): { isValid: boolean; firstInvalidControlId?: string } => {
        const attributes = getAttributesForTab(tabId);

        if (!attributes || attributes.length === 0) {
            return { isValid: true };
        }

        // Use the imported isFormSectionValid function for consistency
        const isValid = isFormSectionValid(attributes, formValues);

        // Find first invalid required field
        let firstInvalidControlId: string | undefined;
        if (!isValid) {
            for (const attr of attributes) {
                if (attr.isRequired) {
                    const value = formValues[attr._id];
                    const isEmpty = value === undefined || value === null ||
                        (typeof value === 'string' && value.trim() === '') ||
                        (Array.isArray(value) && value.length === 0);

                    if (isEmpty) {
                        firstInvalidControlId = attr._id;
                        break;
                    }
                }
            }
        }

        return {
            isValid,
            firstInvalidControlId
        };
    }, [getAttributesForTab, formValues]);

    // Helper: Scroll to and focus the first invalid control
    const scrollToFirstInvalidControl = useCallback((controlId: string) => {
        try {
            // Try multiple selectors to find the control
            const selectors = [
                `[data-control-id="${controlId}"]`,
                `#${controlId}`,
                `[id="${controlId}"]`,
                `[name="${controlId}"]`,
                `input[data-control-id="${controlId}"]`,
                `select[data-control-id="${controlId}"]`,
                `textarea[data-control-id="${controlId}"]`,
                `[aria-describedby*="${controlId}"]`,
                `label[for="${controlId}"]`
            ];

            let element: HTMLElement | null = null;

            for (const selector of selectors) {
                try {
                    const found = document.querySelector(selector);
                    if (found instanceof HTMLElement) {
                        element = found;
                        break;
                    }
                } catch (e) {
                    // Continue to next selector if this one fails (e.g., invalid selector)
                    console.warn(`Selector "${selector}" failed:`, e);
                    continue;
                }
            }

            if (element) {
                const el = element;
                // Calculate offset for better visibility
                const elementRect = el.getBoundingClientRect();
                const absoluteElementTop = elementRect.top + window.pageYOffset;
                const offset = 120; // Account for sticky header

                // Smooth scroll to element
                window.scrollTo({
                    top: Math.max(0, absoluteElementTop - offset),
                    behavior: 'smooth'
                });

                // Focus and highlight the element
                setTimeout(() => {
                    el.focus();
                    el.setAttribute('aria-invalid', 'true');

                    // Add visual highlight
                    // el.style.boxShadow = '0 0 0 3px rgba(220, 38, 38, 0.3)';
                    // el.style.borderColor = '#dc2626';

                    // Remove highlight after 3 seconds
                    setTimeout(() => {
                        el.style.boxShadow = '';
                        el.style.borderColor = '';
                    }, 3000);
                }, 300);

                return true;
            } else {
                console.warn(`Could not find element with control ID: ${controlId}`);
                return false;
            }
        } catch (error) {
            console.error('Error scrolling to invalid control:', error);
            return false;
        }
    }, []);

    const handleTabClick = async (tab: string, index: number) => {

        // Skip validation if clicking on the current active tab
        if (tab === activeTab) {
            return;
        }

        // Calculate if we're currently on Summary (allows editing)
        const isCurrentlyOnSummary = activeTab === toTabKey(ENTITY_NAMES.SUMMARY);

        // Calculate the same isTabNavigationDisabled logic used in the JSX
        const familyAttributes = tabAttributes[ENTITY_NAMES.FAMILY_INFORMATION] || [];
        const shouldSkip = shouldSkipToSummary(familyAttributes, formValues);
        const isParentFinancesTab = tab === toTabKey(ENTITY_NAMES.PARENT_FINANCES);
        const isStudentFinancesTab = tab === toTabKey(ENTITY_NAMES.STUDENT_FINANCES);

        const independentAttribute = familyAttributes.find(attr =>
            attr.name.toLowerCase().includes('independent student')
        );
        const parentAttributes = tabAttributes[ENTITY_NAMES.PARENT_FINANCES] || [];
        const studentAttributes = tabAttributes[ENTITY_NAMES.STUDENT_FINANCES] || tabAttributes[ENTITY_NAMES.STUDENT_INFORMATION] || [];

        const independentValue = independentAttribute ? String(formValues[independentAttribute._id] ?? '').toLowerCase() : null;
        const isIndependent = independentValue === 'yes';
        const isNotIndependent = independentValue === 'no';

        const parentHasData = parentAttributes.some(attr => {
            const v = formValues?.[attr._id];
            return v !== undefined && v !== null && String(v).trim() !== '';
        });
        const studentHasData = studentAttributes.some(attr => {
            const v = formValues?.[attr._id];
            return v !== undefined && v !== null && String(v).trim() !== '';
        });

        // Check if target tab is disabled by skip logic (same as JSX calculation)
        const isTargetTabDisabledBySkip = (() => {
            if (isParentFinancesTab) {
                if (isNotIndependent) return false;
                if (isIndependent) return true;
                if (shouldSkip && !parentHasData && !completedTabs.has(toTabKey(ENTITY_NAMES.PARENT_FINANCES))) return true;
                return false;
            }
            if (isStudentFinancesTab) {
                if (isNotIndependent) return false;
                if (isIndependent) return false;
                if (shouldSkip && !studentHasData && !completedTabs.has(toTabKey(ENTITY_NAMES.STUDENT_FINANCES))) return true;
                return false;
            }
            return false;
        })();

        const isSummaryTabTarget = tab === toTabKey(ENTITY_NAMES.SUMMARY);
        const activeIndex = tabs.indexOf(activeTab);

        // Enhanced navigation logic: Allow navigation from Family Information to any non-disabled tab
        const isFamilyInformationActive = activeTab === toTabKey(ENTITY_NAMES.FAMILY_INFORMATION);
        const isParentFinancesActive = activeTab === toTabKey(ENTITY_NAMES.PARENT_FINANCES);

        // Calculate isTabNavigationDisabled using enhanced logic
        // EXCEPTION: When editing from summary (shouldReturnToSummary), allow navigation to any tab except those disabled by skip logic
        // EXCEPTION: When on Family Information, allow navigation to any tab except those disabled by skip logic
        // EXCEPTION: When on Parent Finances and clicking Student Finances (both should be enabled when independent=No)
        const isBlockedByTabOrder = isSummaryTabTarget
            ? !completedTabs.has(toTabKey(ENTITY_NAMES.SUMMARY))
            : index > activeIndex && !completedTabs.has(tabs[index - 1]);

        let isTabNavigationDisabled: boolean;
        if (shouldReturnToSummary || isFamilyInformationActive) {
            // Only skip logic applies when editing from summary or on family info
            isTabNavigationDisabled = isTargetTabDisabledBySkip;
        } else if (isParentFinancesActive && isStudentFinancesTab && isNotIndependent) {
            // Allow clicking Student Finances from Parent Finances when both are enabled
            isTabNavigationDisabled = false;
        } else {
            isTabNavigationDisabled = isBlockedByTabOrder || isTargetTabDisabledBySkip;
        }

        // FIRST CHECK: Prevent navigation to disabled tabs
        if (isTabNavigationDisabled) {
            console.warn(`Cannot navigate to ${tab} - tab navigation is disabled. Current tab: ${activeTab}, Target: ${tab}, Skip logic: ${isTargetTabDisabledBySkip}`);
            return;
        }

        // SECOND CHECK: Perform validation before allowing navigation 
        // EXCEPTIONS: Skip validation when navigating FROM Summary or FROM Family Information
        // NOTE: Removed shouldReturnToSummary from skip condition - validation should apply even when editing from Summary
        const shouldSkipValidation = isCurrentlyOnSummary || isFamilyInformationActive;

        if (!shouldSkipValidation) {
            // Get current tab attributes for validation
            const currentEntityNames = TAB_ENTITY_MAP[activeTab] || [];
            const currentTabAttributes = currentEntityNames.flatMap(entity => tabAttributes[entity] || []);
            const enabledCurrentTabAttributes = currentTabAttributes.filter(attr => {
                const entityName = currentEntityNames.find(name => {
                    const entityAttrs = tabAttributes[name] || [];
                    return entityAttrs.some(a => a._id === attr._id);
                });
                return !disableLogic(attr, currentTabAttributes, formValues, entityName);
            });

            const currentTabValidation = validateCurrentTab(activeTab);

            if (!currentTabValidation.isValid) {
                // Show validation errors
                const formErrors = validateForm(enabledCurrentTabAttributes, formValues);
                setValidationErrors(formErrors);
                setShowErrors(true);

                // Scroll to first invalid field
                if (currentTabValidation.firstInvalidControlId) {
                    scrollToFirstInvalidControl(currentTabValidation.firstInvalidControlId);
                }

                // Show user-friendly error message
                const errorCount = Object.keys(formErrors).length;
                console.warn(`Cannot navigate to ${tab}. Please complete ${errorCount} required field(s) in ${activeTab}.`);

                // Prevent navigation by returning early
                return;
            }
        }

        // If we've passed all checks, allow navigation
        // Clear validation errors when navigating to a different tab
        setShowErrors(false);
        setValidationErrors({});


        // Load tab data if not already loaded (especially important for tabs that were previously disabled)
        const targetEntityNames = TAB_ENTITY_MAP[tab] || [];

        // Check if tab data needs to be loaded
        const isTabNotLoaded = !loadedTabsRef.current.has(tab);
        const isNavigatingFromParentToStudent = activeTab === toTabKey(ENTITY_NAMES.PARENT_FINANCES) && isStudentFinancesTab;
        const shouldLoadTabData = isTabNotLoaded || isNavigatingFromParentToStudent;



        if (targetEntityNames.length > 0 && shouldLoadTabData) {
            const targetEntityId = getEntityIdByName(targetEntityNames[0]);
            if (targetEntityId) {
                setLoadingNextTab(true);

                // Build payload with current form values
                const currentPayload = buildCalculatePayload(
                    contextDerivedData || estimateBody,
                    formValues,
                    tabAttributes
                );

                // Determine correct entity ID for context
                const currentEntityNames = TAB_ENTITY_MAP[activeTab] || [];
                const currentEntityId = currentEntityNames.length > 0
                    ? getEntityIdByName(currentEntityNames[0])
                    : undefined;

                // Special handling for Parent Finances and Student Finances when clicked from Family Information or Parent Finances
                let contextEntityIdToUse = currentEntityId;
                if (isParentFinancesTab && isFamilyInformationActive) {
                    // Use Family Information entity ID as context for Parent Finances
                    const familyEntityId = getEntityIdByName(ENTITY_NAMES.FAMILY_INFORMATION);
                    if (familyEntityId) {
                        contextEntityIdToUse = familyEntityId;

                    }
                } else if (isStudentFinancesTab) {
                    // When clicking Student Finances, determine context based on previous tab
                    if (isFamilyInformationActive) {
                        // Clicking from Family Information - use Family Info as context
                        const familyEntityId = getEntityIdByName(ENTITY_NAMES.FAMILY_INFORMATION);
                        if (familyEntityId) {
                            contextEntityIdToUse = familyEntityId;

                        }
                    } else {
                        // Clicking from Parent Finances or other tab - use Parent Finances as context
                        const parentFinancesEntityId = getEntityIdByName(ENTITY_NAMES.PARENT_FINANCES);
                        if (parentFinancesEntityId) {
                            contextEntityIdToUse = parentFinancesEntityId;

                        }
                    }
                }

                // Mark Parent Finances as completed when loading it (so Student Finances becomes clickable)
                if (isParentFinancesTab && isFamilyInformationActive && isNotIndependent) {
                    setCompletedTabs(prev => new Set(prev).add(tab));
                }

                // Navigate first to show immediate feedback
                setActiveTab(tab);
                window.scrollTo({ top: 0, behavior: 'smooth' });

                // Load data in background
                try {


                    const response = await calculateDerivedAttributesForEntity(
                        targetEntityId,
                        currentPayload,
                        contextEntityIdToUse
                    );

                    if (response?.objects) {
                        const entityObject = response.objects.find((o: any) =>
                            o.name === targetEntityNames[0]
                        );

                        if (entityObject?.attributes) {
                            const controls = entityObject.attributes.map((a: any) => mapApiAttributeToControl(a, targetEntityNames[0]));

                            handleAttributesFetched(controls, targetEntityNames[0]);
                            loadedTabsRef.current.add(tab);
                        }
                    }
                } catch (error) {
                    console.error(`âŒ Failed to load data for tab ${tab}:`, error);
                    setValidationErrors(prev => ({
                        ...prev,
                        tabLoad: `Failed to load data for this tab. Please try again.`
                    }));
                } finally {
                    setLoadingNextTab(false);
                }

                return; // Exit early since we already navigated
            }
        }

        // If tab data already loaded, call calculate-derived-attributes before navigating
        if (targetEntityNames.length > 0) {
            const targetEntityId = getEntityIdByName(targetEntityNames[0]);
            if (targetEntityId) {
                try {
                    // Build payload with current form values
                    const currentPayload = buildCalculatePayload(
                        contextDerivedData || estimateBody,
                        formValues,
                        tabAttributes
                    );

                    // Determine correct entity ID for context
                    const currentEntityNames = TAB_ENTITY_MAP[activeTab] || [];
                    const currentEntityId = currentEntityNames.length > 0
                        ? getEntityIdByName(currentEntityNames[0])
                        : undefined;

                    // Call calculate-derived-attributes API on tab click
                    await calculateDerivedAttributesForEntity(
                        targetEntityId,
                        currentPayload,
                        currentEntityId
                    );
                } catch (error) {
                    console.error(`Error calling calculate-derived-attributes on tab click for ${tab}:`, error);
                }
            }
        }

        // Navigate to the tab
        setActiveTab(tab);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleNext = async () => {
        const entityNames = TAB_ENTITY_MAP[activeTab] || [];
        const allTabAttributes = entityNames.flatMap(entity => tabAttributes[entity] || []);
        // Use allAttributes (all loaded tabs) so cross-tab enabling conditions are resolved correctly.
        const enabledTabAttributes = allTabAttributes.filter(attr => evaluateEnablingCondition(attr, allAttributes, formValues));

        // Debug log for Family Information specific navigation flow
        if (activeTab === toTabKey(ENTITY_NAMES.FAMILY_INFORMATION)) {

            // const independentValue = independentAttribute ? formValues[independentAttribute._id] : undefined;


        }

        // Call calculate-derived-attributes API on every Continue button click
        const currentEntityNames = TAB_ENTITY_MAP[activeTab] || [];
        for (const entityName of currentEntityNames) {
            let entityId = getEntityIdByName(entityName);
            if (entityId) {
                try {

                    // Determine correct entity ID to pass as context
                    const isFamilyInfoTab = activeTab === toTabKey(ENTITY_NAMES.FAMILY_INFORMATION);
                    const isParentFinancesTab = activeTab === toTabKey(ENTITY_NAMES.PARENT_FINANCES);

                    // For Family Information tab: use next destination entity ID based on independent student status
                    if (isFamilyInfoTab) {
                        const familyAttributes = tabAttributes[ENTITY_NAMES.FAMILY_INFORMATION] || [];
                        const independentAttribute = familyAttributes.find(attr =>
                            attr.name?.toLowerCase().includes('independent student') ||
                            attr.name?.toLowerCase().includes('are you an independent student')
                        );

                        if (independentAttribute) {
                            const independentValue = String(formValues[independentAttribute._id] ?? '');


                            // Check if user selected "Yes" for independent student
                            if (independentValue.toLowerCase() === 'yes') {
                                const studentFinancesEntityId = getEntityIdByName(ENTITY_NAMES.STUDENT_FINANCES);
                                if (studentFinancesEntityId) {
                                    // CHANGE BOTH: the API URL entityId AND the header entityId
                                    entityId = studentFinancesEntityId; // This changes the API URL path

                                }
                            }
                            // Check if user selected "No" for independent student (dependent)
                            else if (independentValue.toLowerCase() === 'no') {
                                const parentFinancesEntityId = getEntityIdByName(ENTITY_NAMES.PARENT_FINANCES);
                                if (parentFinancesEntityId) {
                                    // CHANGE BOTH: the API URL entityId AND the header entityId
                                    entityId = parentFinancesEntityId; // This changes the API URL path

                                }
                            }
                        }
                    }

                    // Special case for Parent Finances: when user clicks continue from parent finances,
                    // use Student Finances entity ID for both URL and header
                    if (isParentFinancesTab) {
                        const studentFinancesEntityId = getEntityIdByName(ENTITY_NAMES.STUDENT_FINANCES);
                        if (studentFinancesEntityId) {
                            // CHANGE BOTH: the API URL entityId AND the header entityId
                            entityId = studentFinancesEntityId; // This changes the API URL path

                        }
                    }

                } catch (err) {
                    console.error(`Error calling calculate-derived-attributes for ${entityName}:`, err);
                    // Continue navigation even if API call fails
                }
            }
        }

        const formErrors = validateForm(enabledTabAttributes, formValues);
        setValidationErrors(formErrors);
        setShowErrors(true); // Always show errors after a continue attempt


        // Check if we should return to Summary after updating
        if (Object.keys(formErrors).length === 0 && shouldReturnToSummary) {
            // Reload finance tabs if needed before returning to summary
            try {
                setLoadingNextTab(true);

                // Build payload with current form values
                const currentPayload = buildCalculatePayload(
                    estimateBody,
                    formValues,
                    tabAttributes
                );

                // Get current entity ID
                const currentEntityNames = TAB_ENTITY_MAP[activeTab] || [];
                const currentEntityId = currentEntityNames.length > 0
                    ? getEntityIdByName(currentEntityNames[0])
                    : undefined;

                // Call calculate-derived-attributes for current entity before returning to Summary
                if (currentEntityId) {
                    try {
                        await calculateDerivedAttributesForEntity(
                            currentEntityId,
                            currentPayload,
                            currentEntityId
                        );
                    } catch (err) {
                        console.error(`Error calling calculate-derived-attributes for current entity before returning to Summary:`, err);
                    }
                }

                // Check if we need to reload finance tabs based on form values
                const familyAttributes = tabAttributes[ENTITY_NAMES.FAMILY_INFORMATION] || [];
                const shouldSkipFinances = shouldSkipToSummary(familyAttributes, formValues);

                // Clear loaded status for finance tabs to force reload
                const parentFinancesTabKey = toTabKey(ENTITY_NAMES.PARENT_FINANCES);
                const studentFinancesTabKey = toTabKey(ENTITY_NAMES.STUDENT_FINANCES);

                loadedTabsRef.current.delete(parentFinancesTabKey);
                loadedTabsRef.current.delete(studentFinancesTabKey);

                // CRITICAL: Call THREE APIs when user returns to summary after editing family information
                // and has selected "Help me calculate my need-based financial aid"
                // This ensures all finance data is loaded on the summary page
                const parentFinancesEntityId = getEntityIdByName(ENTITY_NAMES.PARENT_FINANCES);
                const studentFinancesEntityId = getEntityIdByName(ENTITY_NAMES.STUDENT_FINANCES);
                const familyEntityId = getEntityIdByName(ENTITY_NAMES.FAMILY_INFORMATION);




                // API Call 1: Parent Finances entity ID
                if (parentFinancesEntityId) {
                    try {
                        const parentResponse = await calculateDerivedAttributesForEntity(
                            parentFinancesEntityId,
                            currentPayload,
                            currentEntityId
                        );

                        if (parentResponse?.objects) {
                            const parentObject = parentResponse.objects.find((o: any) =>
                                o.name === ENTITY_NAMES.PARENT_FINANCES
                            );
                            if (parentObject?.attributes) {
                                const controls = parentObject.attributes.map((a: any) => mapApiAttributeToControl(a, ENTITY_NAMES.PARENT_FINANCES));
                                handleAttributesFetched(controls, ENTITY_NAMES.PARENT_FINANCES);
                                loadedTabsRef.current.add(parentFinancesTabKey);
                            }
                        }
                    } catch (error) {
                        console.error('âŒ API Call 1: Failed to reload Parent Finances:', error);
                    }
                }

                // API Call 2: Student Finances entity ID
                if (studentFinancesEntityId) {
                    try {
                        const studentFinResponse = await calculateDerivedAttributesForEntity(
                            studentFinancesEntityId,
                            currentPayload,
                            currentEntityId
                        );

                        if (studentFinResponse?.objects) {
                            const studentFinObject = studentFinResponse.objects.find((o: any) =>
                                o.name === ENTITY_NAMES.STUDENT_FINANCES
                            );

                            if (studentFinObject?.attributes) {
                                const controls = studentFinObject.attributes.map((a: any) => mapApiAttributeToControl(a, ENTITY_NAMES.STUDENT_FINANCES));
                                handleAttributesFetched(controls, ENTITY_NAMES.STUDENT_FINANCES);
                                loadedTabsRef.current.add(studentFinancesTabKey);
                            }
                        }
                    } catch (error) {
                        console.error('API Call 2: Failed to reload Student Finances:', error);
                    }
                } else {
                    console.warn('Student Finances Entity ID not found - skipping API call');
                }

                // When "Help me calculate" is selected (!shouldSkipFinances), also call Family Information API
                if (!shouldSkipFinances) {

                    // API Call 3: Family Information entity ID
                    if (familyEntityId) {
                        try {
                            await calculateDerivedAttributesForEntity(
                                familyEntityId,
                                currentPayload,
                                familyEntityId
                            );
                        } catch (error) {
                            console.error('âŒ API Call 3: Failed to reload Family Information:', error);
                        }
                    }
                }


                // Force re-render by updating completedTabs
                setCompletedTabs(prev => new Set(prev));

            } catch (error) {
                console.error('Failed to reload finance data:', error);
            } finally {
                setLoadingNextTab(false);
            }

            setShouldReturnToSummary(false);
            setActiveTab(toTabKey(ENTITY_NAMES.SUMMARY));
            setShowErrors(false);
            setValidationErrors({});
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        if (Object.keys(formErrors).length === 0 && activeIndex < tabs.length - 1) {
            setCompletedTabs(prev => new Set(prev).add(activeTab));

            // Special handling for Student Information: resolve high school CEEB before building payload
            // (No separate Estimate API call â€” the next-tab call below handles both Academic Scores
            //  controls and Estimate data in a single request.)
            if (activeTab === toTabKey(ENTITY_NAMES.STUDENT_INFORMATION)) {
                try {
                    const studentAttributes = tabAttributes[ENTITY_NAMES.STUDENT_INFORMATION] || [];
                    const highSchoolAttr = studentAttributes.find(attr => attr.name.toLowerCase().includes('high school'));
                    if (highSchoolAttr && formValues[highSchoolAttr._id]) {
                        const hsValue = formValues[highSchoolAttr._id];
                        let ceebValue: string | number = hsValue;

                        if (!/^\d+$/.test(hsValue)) {
                            try {
                                const tenantName = localStorage.getItem('TENANT_NAME') ?? '';
                                const normalizedTenantName = tenantName.trim().toLowerCase();
                                const hsUrl = `https://dev.netpriceestimate.com/api/highschools?schooltext=${encodeURIComponent(hsValue)}`;
                                const highschoolsResponse = await fetch(hsUrl, {
                                    headers: { 'authority-name': normalizedTenantName }
                                });
                                const highschoolsData = await highschoolsResponse.json();
                                const matchingSchool = highschoolsData.find((school: any) => school.hsName === hsValue);
                                if (matchingSchool?.ceeb) {
                                    ceebValue = matchingSchool.ceeb;
                                } else {
                                    console.info('No matching school found or ceeb missing for high school search:', { hsName: hsValue });
                                }
                            } catch (err) {
                                console.error('Failed to fetch highschools API for:', hsValue, err);
                            }
                        }

                        // Update formValues to use ceeb for the POST payload
                        formValues[highSchoolAttr._id] = String(ceebValue);
                    }
                } catch (error) {
                    console.error('Failed to resolve high school CEEB:', error);
                }
            }

            // Build payload with current form values for ALL entities up to this point
            const currentPayload = buildCalculatePayload(
                contextDerivedData || estimateBody,
                formValues,
                tabAttributes
            );

            // Helper function to determine next tab based on user selections
            const getNextTabKey = (): string => {
                const currentTabKey = activeTab;

                // If on Family Information tab
                if (currentTabKey === toTabKey(ENTITY_NAMES.FAMILY_INFORMATION)) {
                    const familyAttributes = tabAttributes[ENTITY_NAMES.FAMILY_INFORMATION] || [];

                    // Check if student is independent
                    const independentAttribute = familyAttributes.find(attr =>
                        attr.name.toLowerCase().includes('independent student')
                    );
                    const independentValue = independentAttribute ? String(formValues[independentAttribute._id] ?? '') : '';
                    const isIndependent = independentValue.toLowerCase() === 'yes';

                    // Find the aid calculation attribute - try multiple possible names
                    const aidCalculationAttribute = familyAttributes.find(attr =>
                        attr.name === "Student Aid Preference" ||
                        attr.name?.toLowerCase().includes('please select an option') ||
                        attr.name?.toLowerCase().includes('calculation') ||
                        attr.possibleValues?.some((pv: any) =>
                            typeof pv === 'string' && pv.toLowerCase().includes('help me calculate')
                        )
                    );


                    if (aidCalculationAttribute) {
                        const selectedOption = formValues[aidCalculationAttribute._id];

                        // If user selected "Help me calculate" option
                        if (selectedOption === AID_CALCULATION_OPTIONS.NEED_BASED) {
                            // Independent students skip Parent Finances
                            if (isIndependent) {
                                setFinancesWereSkipped(false);
                                tabStateStorage.setFinancesSkipped(false);
                                return toTabKey(ENTITY_NAMES.STUDENT_FINANCES);
                            } else {
                                // Dependent students go to Parent Finances
                                setFinancesWereSkipped(false);
                                tabStateStorage.setFinancesSkipped(false);
                                return toTabKey(ENTITY_NAMES.PARENT_FINANCES);
                            }
                        } else if (isIndependent) {
                            // User selected SAI/International/Skip
                            // CRITICAL: Independent students ALWAYS go to Student Finances, even if skip options selected
                            setFinancesWereSkipped(false);
                            tabStateStorage.setFinancesSkipped(false);
                            return toTabKey(ENTITY_NAMES.STUDENT_FINANCES);
                        } else if (shouldSkipToSummary(familyAttributes, formValues)) {
                            // Dependent students skip to Summary
                            setFinancesWereSkipped(true);
                            tabStateStorage.setFinancesSkipped(true);
                            return toTabKey(ENTITY_NAMES.SUMMARY);
                        } else {
                            // Dependent students go to Parent Finances
                            setFinancesWereSkipped(false);
                            tabStateStorage.setFinancesSkipped(false);
                            return toTabKey(ENTITY_NAMES.PARENT_FINANCES);
                        }
                    } else if (isIndependent) {
                        // Legacy fallback logic
                        // CRITICAL: Independent students ALWAYS go to Student Finances first
                        setFinancesWereSkipped(false);
                        tabStateStorage.setFinancesSkipped(false);
                        return toTabKey(ENTITY_NAMES.STUDENT_FINANCES);
                    } else if (shouldSkipToSummary(familyAttributes, formValues)) {
                        setFinancesWereSkipped(true);
                        tabStateStorage.setFinancesSkipped(true);
                        return toTabKey(ENTITY_NAMES.SUMMARY);
                    } else {
                        setFinancesWereSkipped(false);
                        tabStateStorage.setFinancesSkipped(false);
                        return toTabKey(ENTITY_NAMES.PARENT_FINANCES);
                    }
                }

                // If on Parent Finances tab - always go to Student Finances next
                if (currentTabKey === toTabKey(ENTITY_NAMES.PARENT_FINANCES)) {
                    return toTabKey(ENTITY_NAMES.STUDENT_FINANCES);
                }

                // If on Student Finances tab - always go to Summary next
                if (currentTabKey === toTabKey(ENTITY_NAMES.STUDENT_FINANCES)) {
                    return toTabKey(ENTITY_NAMES.SUMMARY);
                }

                // Default sequential navigation for other tabs
                const currentIndex = tabs.findIndex(tab => tab === currentTabKey);
                if (currentIndex !== -1 && currentIndex < tabs.length - 1) {
                    return tabs[currentIndex + 1];
                }

                return toTabKey(ENTITY_NAMES.SUMMARY);
            };

            const nextTabKey = getNextTabKey();
            let nextTabIndex = tabs.findIndex(tab => tab === nextTabKey);

            // If tab not found, default to next sequential tab
            if (nextTabIndex === -1) {
                nextTabIndex = activeIndex + 1;
            }

            // Pre-fetch Parent Finances if navigating to it
            if (nextTabKey === toTabKey(ENTITY_NAMES.PARENT_FINANCES)) {
                const parentFinancesEntityId = getEntityIdByName(ENTITY_NAMES.PARENT_FINANCES);
                if (parentFinancesEntityId && !loadedTabsRef.current.has(toTabKey(ENTITY_NAMES.PARENT_FINANCES))) {
                    setLoadingNextTab(true);
                    try {

                    } catch (err) {
                        console.error('Failed to pre-fetch Parent Finances:', err);
                    } finally {
                        setLoadingNextTab(false);
                    }
                }
            }

            // Continue with next part of the function to ensure other tabs are pre-fetched properly
            const isFamilyInfoTab = activeTab === toTabKey(ENTITY_NAMES.FAMILY_INFORMATION);
            if (isFamilyInfoTab) {
                // Only proceed to additional pre-fetch logic if we're not going to Parent Finances
                if (nextTabKey !== toTabKey(ENTITY_NAMES.PARENT_FINANCES)) {
                    const parentFinancesEntityId = getEntityIdByName(ENTITY_NAMES.PARENT_FINANCES);

                    // Legacy pre-fetch code for other scenarios
                    if (parentFinancesEntityId && !loadedTabsRef.current.has(toTabKey(ENTITY_NAMES.PARENT_FINANCES))) {
                        setLoadingNextTab(true);
                        try {

                        } catch (err) {
                            console.error('Failed to pre-fetch Parent Finances (fallback):', err);
                        } finally {
                            setLoadingNextTab(false);
                        }
                    }
                }
            }

            const finalNextTabKey = tabs[nextTabIndex];

            // If the next tab is Summary, perform the final calculation before navigating.
            if (finalNextTabKey === toTabKey(ENTITY_NAMES.SUMMARY)) {
                setLoadingNextTab(true);
                try {
                    // Load all entity data before showing summary

                    // Load Family Information if not already loaded
                    const familyEntityId = getEntityIdByName(ENTITY_NAMES.FAMILY_INFORMATION);
                    if (familyEntityId && !loadedTabsRef.current.has(toTabKey(ENTITY_NAMES.FAMILY_INFORMATION))) {
                        const familyResponse = await calculateDerivedAttributesForEntity(familyEntityId, currentPayload, currentEntityId);
                        if (familyResponse?.objects) {
                            const familyObject = familyResponse.objects.find((o: any) => o.name === ENTITY_NAMES.FAMILY_INFORMATION);
                            if (familyObject?.attributes) {
                                const controls = familyObject.attributes.map((a: any) => mapApiAttributeToControl(a, ENTITY_NAMES.FAMILY_INFORMATION));
                                handleAttributesFetched(controls, ENTITY_NAMES.FAMILY_INFORMATION);
                                loadedTabsRef.current.add(toTabKey(ENTITY_NAMES.FAMILY_INFORMATION));
                            }
                        }
                    }

                    // Load Parent Finances if not already loaded AND finances were not skipped
                    const familyAttributes = tabAttributes[ENTITY_NAMES.FAMILY_INFORMATION] || [];
                    const shouldSkipFinances = shouldSkipToSummary(familyAttributes, formValues);
                    const parentFinancesEntityId = getEntityIdByName(ENTITY_NAMES.PARENT_FINANCES);
                    if (parentFinancesEntityId && !loadedTabsRef.current.has(toTabKey(ENTITY_NAMES.PARENT_FINANCES)) && !shouldSkipFinances) {
                        try {
                            const parentFinancesContextId = getEntityIdByName(ENTITY_NAMES.FAMILY_INFORMATION) ?? currentEntityId;
                            const parentResponse = await calculateDerivedAttributesForEntity(parentFinancesEntityId, currentPayload, parentFinancesContextId);
                            if (parentResponse?.objects) {
                                const parentObject = parentResponse.objects.find((o: any) => o.name === ENTITY_NAMES.PARENT_FINANCES);
                                if (parentObject?.attributes) {
                                    const controls = parentObject.attributes.map((a: any) => ({
                                        _id: a._id,
                                        controlId: a._id,
                                        controlName: a.name,
                                        name: a.name,
                                        displayName: a.friendlyName || a.name,
                                        dataType: a.dataType || 'String',
                                        sequence: typeof a.order === 'number' ? a.order : 0,
                                        visible: true,
                                        onclauseEnable: false,
                                        required: !!a.isRequired,
                                        screenName: ENTITY_NAMES.PARENT_FINANCES,
                                        tenant: '',
                                        description: a.description || '',
                                        isRequired: !!a.isRequired,
                                        isMultivalued: !!a.isMultivalued,
                                        enablingAttribute: a.enablingAttribute,
                                        enablingValue: a.enablingValue,
                                        possibleValues: a.possibleValues,
                                        possibleValuesSource: a.possibleValuesSource,
                                        possibleMinValue: a.possibleMinValue,
                                        possibleMaxValue: a.possibleMaxValue,
                                        possibleRangeStep: a.possibleRangeStep,
                                        controltype: a.isInformational ? 'label' : (a.possibleValues ? 'dropdown' : 'textinput') as any,
                                    }));
                                    handleAttributesFetched(controls, ENTITY_NAMES.PARENT_FINANCES);
                                    loadedTabsRef.current.add(toTabKey(ENTITY_NAMES.PARENT_FINANCES));
                                }
                            }
                        } catch (err) {
                            console.error('Failed to load Parent Finances for Summary:', err);
                        }
                    }

                    // Load Student Finances if not already loaded AND finances were not skipped
                    const studentFinancesEntityId = getEntityIdByName(ENTITY_NAMES.STUDENT_FINANCES);
                    if (studentFinancesEntityId && !loadedTabsRef.current.has(toTabKey(ENTITY_NAMES.STUDENT_FINANCES)) && !shouldSkipFinances) {
                        try {
                            const studentFinancesContextId = getEntityIdByName(ENTITY_NAMES.PARENT_FINANCES) ?? currentEntityId;
                            const studentFinResponse = await calculateDerivedAttributesForEntity(studentFinancesEntityId, currentPayload, studentFinancesContextId);
                            if (studentFinResponse?.objects) {
                                const studentFinObject = studentFinResponse.objects.find((o: any) => o.name === ENTITY_NAMES.STUDENT_FINANCES);
                                if (studentFinObject?.attributes) {
                                    const controls = studentFinObject.attributes.map((a: any) => ({
                                        _id: a._id,
                                        controlId: a._id,
                                        controlName: a.name,
                                        name: a.name,
                                        displayName: a.friendlyName || a.name,
                                        dataType: a.dataType || 'String',
                                        sequence: typeof a.order === 'number' ? a.order : 0,
                                        visible: true,
                                        onclauseEnable: false,
                                        required: !!a.isRequired,
                                        screenName: ENTITY_NAMES.STUDENT_FINANCES,
                                        tenant: '',
                                        description: a.description || '',
                                        isRequired: !!a.isRequired,
                                        isMultivalued: !!a.isMultivalued,
                                        enablingAttribute: a.enablingAttribute,
                                        enablingValue: a.enablingValue,
                                        possibleValues: a.possibleValues,
                                        possibleValuesSource: a.possibleValuesSource,
                                        possibleMinValue: a.possibleMinValue,
                                        possibleMaxValue: a.possibleMaxValue,
                                        possibleRangeStep: a.possibleRangeStep,
                                        controltype: a.isInformational ? 'label' : (a.possibleValues ? 'dropdown' : 'textinput') as any,
                                    }));
                                    handleAttributesFetched(controls, ENTITY_NAMES.STUDENT_FINANCES);
                                    loadedTabsRef.current.add(toTabKey(ENTITY_NAMES.STUDENT_FINANCES));
                                }
                            }
                        } catch (err) {
                            console.error('Failed to load Student Finances for Summary:', err);
                        }
                    }





                    // Load Academic Scores if not already loaded
                    const academicEntityId = getEntityIdByName(ENTITY_NAMES.ACADEMIC_SCORES);
                    if (academicEntityId && !loadedTabsRef.current.has(toTabKey(ENTITY_NAMES.ACADEMIC_SCORES))) {
                        const academicResponse = await calculateDerivedAttributesForEntity(academicEntityId, currentPayload, currentEntityId);
                        if (academicResponse?.objects) {
                            const academicObject = academicResponse.objects.find((o: any) => o.name === ENTITY_NAMES.ACADEMIC_SCORES);
                            if (academicObject?.attributes) {
                                /**
                                 * Wrapper function to process controls and hide specific fields initially
                                 * @param attributes - Array of attribute objects from API
                                 * @returns Array of processed controls with visibility settings
                                 */
                                const processAcademicControls = (attributes: any[]) => {
                                    const hiddenFields = ["Your ACT Score", "Your SAT Score"];
                                    return attributes.map((a: any) => ({
                                        ...mapApiAttributeToControl(a, ENTITY_NAMES.ACADEMIC_SCORES),
                                        visible: !hiddenFields.includes(a.name),
                                        defaultValue: a.defaultValue ?? a.value,
                                        value: a.value ?? null,
                                    }));
                                };

                                const controls = processAcademicControls(academicObject.attributes);
                                handleAttributesFetched(controls, ENTITY_NAMES.ACADEMIC_SCORES);
                                loadedTabsRef.current.add(toTabKey(ENTITY_NAMES.ACADEMIC_SCORES));
                            }
                        }
                    }

                    // Now load Summary entity
                    const summaryEntityId = getEntityIdByName(ENTITY_NAMES.SUMMARY);
                    if (!summaryEntityId) {
                        throw new Error("Summary entity ID not found.");
                    }

                    const summaryResponse = await calculateDerivedAttributesForEntity(
                        summaryEntityId,
                        currentPayload,
                        currentEntityId
                    );

                    // Process the response to populate summary attributes.
                    if (summaryResponse?.objects) {
                        const summaryObject = summaryResponse.objects.find((o: any) => o.name === ENTITY_NAMES.SUMMARY);
                        const estimateObject = summaryResponse.objects.find((o: any) => o.name === ENTITY_NAMES.ESTIMATE);
                        const summaryAttrs = summaryObject?.attributes || [];
                        const estimateAttrs = estimateObject?.attributes || [];
                        const merged = [...summaryAttrs, ...estimateAttrs];

                        if (merged.length > 0) {
                            const controls = merged.map((a: any) => mapApiAttributeToControl(a, ENTITY_NAMES.SUMMARY));
                            // Use handleAttributesFetched to update state correctly.
                            handleAttributesFetched(controls, ENTITY_NAMES.SUMMARY);
                        }
                    }

                    loadedTabsRef.current.add(finalNextTabKey);
                    // Mark Summary as reached so UI reflects skipped tabs correctly
                    setCompletedTabs(prev => new Set(prev).add(finalNextTabKey));
                    setActiveTab(finalNextTabKey); // Navigate to Summary tab.
                } catch (error) {
                    console.error('Failed to calculate and load Summary data:', error);
                    setValidationErrors(prev => ({ ...prev, summaryLoad: 'Failed to load summary. Please try again.' }));
                } finally {
                    setLoadingNextTab(false);
                }
                return; // Exit handleNext after handling summary.
            }

            // For non-summary tabs, load next tab data with accumulated form values
            setLoadingNextTab(true);

            // Always navigate to next tab first, then load data in background
            setActiveTab(finalNextTabKey);
            setShowErrors(false);
            setValidationErrors({});
            window.scrollTo({ top: 0, behavior: 'smooth' });

            try {
                const nextEntityNames = TAB_ENTITY_MAP[finalNextTabKey] || [];
                if (nextEntityNames.length > 0 && !loadedTabsRef.current.has(finalNextTabKey)) {
                    const nextEntityId = getEntityIdByName(nextEntityNames[0]);
                    if (nextEntityId) {
                        // When navigating from Student Information or Academic Scores, pass
                        // the Estimate entity ID as context so the server returns estimate data
                        // alongside the next tab. Academic Scores changes affect merit scholarship,
                        // so the Estimate must be refreshed when the user advances from that tab.
                        const isFromStudentInfo = activeTab === toTabKey(ENTITY_NAMES.STUDENT_INFORMATION);
                        const isFromAcademicScores = activeTab === toTabKey(ENTITY_NAMES.ACADEMIC_SCORES);
                        const estimateEntityId = (isFromStudentInfo || isFromAcademicScores)
                            ? getEntityIdByName(ENTITY_NAMES.ESTIMATE)
                            : undefined;
                        const contextId = estimateEntityId ?? currentEntityId;

                        const tabResponse = await calculateDerivedAttributesForEntity(
                            nextEntityId,
                            currentPayload,
                            contextId
                        );
                        loadedTabsRef.current.add(finalNextTabKey);

                        // When coming from Student Information or Academic Scores, extract Estimate
                        // attributes so the dynamic award updates immediately on Next click.
                        if ((isFromStudentInfo || isFromAcademicScores) && tabResponse?.objects) {
                            const estimateObject = tabResponse.objects.find((o: any) => o.name === ENTITY_NAMES.ESTIMATE);
                            if (estimateObject?.attributes) {
                                const controls = estimateObject.attributes.map((a: any) => mapApiAttributeToControl(a, ENTITY_NAMES.ESTIMATE));
                                handleAttributesFetched(controls, ENTITY_NAMES.ESTIMATE);
                            }
                            setEstimateCalculated(true);
                        }
                    }
                }
            } catch (error) {
                console.error(`Failed to load data for ${finalNextTabKey}:`, error);
                setValidationErrors(prev => ({
                    ...prev,
                    tabLoad: `Failed to load data for this tab. Please try again.`
                }));
            } finally {
                setLoadingNextTab(false);
            }
        }
    };
    const handleBack = async () => {
        // NOTE: The calculate-derived-attributes API call was removed from here.
        // Calling it on Back overwrites contextDerivedData/estimateBody with a
        // tab-scoped response, which then corrupts buildCalculatePayload on the
        // next forward pass and produces a different COA value.
        // loadedTabsRef.current.delete(activeTab) below already guarantees a
        // fresh API call with the correct full payload when the user goes forward.

        if (activeIndex > 0) {
            let backTabIndex = activeIndex - 1;

            // Special handling for Summary tab navigation
            const isSummaryTab = activeTab === toTabKey(ENTITY_NAMES.SUMMARY);

            if (isSummaryTab) {
                // Add the summary tab to completedTabs when going back from it
                setCompletedTabs(prev => {
                    const newSet = new Set(prev);
                    newSet.add(activeTab);
                    return newSet;
                });

                // Check if finances were skipped via SAI/International option
                if (financesWereSkipped) {
                    // Navigate to Family Information (finances were skipped)
                    const familyTabIndex = tabs.indexOf(toTabKey(ENTITY_NAMES.FAMILY_INFORMATION));
                    if (familyTabIndex !== -1) {
                        backTabIndex = familyTabIndex;
                        // Track when user navigates back from Summary to Family Information
                        // setHasReturnedToFamilyInfo(true);
                    }
                } else {
                    // Navigate to Student Finances (normal flow or independent student)
                    const studentFinancesTabIndex = tabs.indexOf(toTabKey(ENTITY_NAMES.STUDENT_FINANCES));
                    if (studentFinancesTabIndex !== -1) {
                        backTabIndex = studentFinancesTabIndex;
                    }
                }
            }

            // Special handling when going back from Student Finances
            if (activeTab === toTabKey(ENTITY_NAMES.STUDENT_FINANCES)) {
                const familyAttributes = tabAttributes[ENTITY_NAMES.FAMILY_INFORMATION] || [];
                const independentAttribute = familyAttributes.find(attr =>
                    attr.name.toLowerCase().includes('independent student')
                );
                const isIndependent = independentAttribute && formValues[independentAttribute._id] === 'Yes';

                // If student is independent, skip Parent Finances and go to Family Information
                if (isIndependent) {
                    const familyTabIndex = tabs.indexOf(toTabKey(ENTITY_NAMES.FAMILY_INFORMATION));
                    if (familyTabIndex !== -1) {
                        backTabIndex = familyTabIndex;
                    }
                }
                // If dependent, normal back navigation (to Parent Finances)
            }

            // Invalidate the current tab's loaded status so navigating forward from the
            // previous tab triggers a fresh API call with any updated form values.
            // This fixes the bug where going back then forward skips recalculation.
            loadedTabsRef.current.delete(activeTab);

            setActiveTab(tabs[backTabIndex]);
            setShowErrors(false); // Don't show errors on previously completed tabs
            setValidationErrors({});
        }
    };

    const handleConnectWithUs = useCallback(() => {
        try {
            // Extract URL from Summary entity's "Connect With Us" attribute in contextDerivedData
            const summaryObject = contextDerivedData?.objects?.find((obj: any) => obj.name === ENTITY_NAMES.SUMMARY);

            if (!summaryObject?.attributes) {
                console.error('Summary entity not found in contextDerivedData');
                return;
            }

            const connectAttribute = summaryObject.attributes.find(
                (attr: any) => attr.name === ATTRIBUTE_NAMES.CONNECT_WITH_US ||
                    attr.name?.toLowerCase().includes(ATTRIBUTE_NAMES.CONNECT_WITH_US.toLowerCase()) ||
                    attr.friendlyName?.toLowerCase().includes(ATTRIBUTE_NAMES.CONNECT_WITH_US.toLowerCase())
            );

            if (!connectAttribute) {
                console.error(`${ATTRIBUTE_NAMES.CONNECT_WITH_US} attribute not found in Summary entity`);
                return;
            }

            // Prioritize defaultValue over constantValue
            const urlValue = connectAttribute.defaultValue ?? connectAttribute.constantValue;

            if (!urlValue) {
                console.error(`${ATTRIBUTE_NAMES.CONNECT_WITH_US} URL value is empty`);
                return;
            }

            const url = urlValue.trim();

            // Security validation: Ensure valid HTTP or HTTPS URL
            const urlObj = new URL(url);
            if (urlObj.protocol !== 'https:' && urlObj.protocol !== 'http:') {
                console.error('Invalid URL protocol - only HTTP/HTTPS URLs are allowed');
                return;
            }

            // Open in a new tab/window
            window.open(url, '_blank');
        } catch (error) {
            console.error('Invalid URL provided for Connect With Us:', error);
        }
    }, [contextDerivedData]);

    const faqNameMap = useMemo(() => {
        // Look for FAQ objects in the derived attributes payload (these are returned by the estimates API)
        const faqEntities = contextDerivedData?.objects?.filter((o: any) => typeof o.name === 'string' && o.name.endsWith(FAQ_SUFFIX)) || [];
        const map: { [key: string]: string } = {};

        for (const faqEntity of faqEntities) {
            const baseName = faqEntity.name.replace(FAQ_SUFFIX, "");
            // Find the tab key that corresponds to this baseName
            for (const [tabKey, entityNames] of Object.entries(TAB_ENTITY_MAP)) {
                if (entityNames.includes(baseName)) {
                    map[tabKey] = faqEntity.name;
                    break;
                }
            }
        }

        return map;
    }, [contextDerivedData]);

    // Global form validity for submit button
    const isGlobalValid = useMemo(() => isFormSectionValid(allAttributes, formValues), [allAttributes, formValues]);

    const evaluateEnablingCondition = React.useCallback((attribute: Control, allSectionAttributes: Control[], values: Record<string, string>): boolean => {
        const { enablingAttribute } = attribute;
        let { enablingValue } = attribute; // Make mutable to allow fallback

        if (!enablingAttribute) {
            return true; // Always enabled/visible if no condition
        }

        const enablingControl = allSectionAttributes.find(
            (attr) => attr.name?.trim().toLowerCase() === enablingAttribute?.trim().toLowerCase()
        );

        if (!enablingControl) {
            return false; // Can't be enabled if its controller is not found
        }

        // If enablingValue is not provided (null/undefined/empty), prefer the enablingControl's
        // defaultValue when available. If no explicit enablingValue or default exists,
        // treat it as no-condition (i.e., show the dependent control) because the attribute
        // isn't expressing a meaningful dependency.
        if (enablingValue == null || enablingValue === '') {
            if (enablingControl.defaultValue !== undefined && enablingControl.defaultValue !== null && enablingControl.defaultValue !== '') {
                enablingValue = enablingControl.defaultValue;
            } else {
                // No enablingValue and no controller default -> treat as no condition
                return true;
            }
        }

        let enablingControlValue = values[enablingControl._id];

        // If not yet set, attempt reasonable fallbacks:
        // - If the enabling control exposes a defaultValue, use it.
        // - If the enabling control is Boolean, assume 'false' (unchecked) so dependents
        //   that show when the controller is false are visible by default.
        // - Otherwise treat the condition as indeterminate and keep the dependent hidden.
        if (enablingControlValue === undefined || enablingControlValue === null || enablingControlValue === '') {
            if (enablingControl.defaultValue !== undefined && enablingControl.defaultValue !== null && enablingControl.defaultValue !== '') {
                enablingControlValue = enablingControl.defaultValue;
            } else if (enablingControl.dataType === 'Boolean') {
                enablingControlValue = 'false';
            } else {
                // No value, no useful default -> do not enable the dependent control
                return false;
            }
        }

        // Handle boolean (checkbox) logic
        if (enablingControl.dataType === 'Boolean') {
            const actualValue = (enablingControlValue === 'true' || enablingControlValue === 'True') ? 'true' : 'false';
            return actualValue === enablingValue;
        }

        // Handle dropdowns where the stored value might be a shortened version (e.g., 'Yes')
        if (enablingControl.isMultivalued && typeof enablingValue === 'string' && typeof enablingControlValue === 'string') {
            // Trim both values to handle potential whitespace/newline differences from the API
            if (enablingValue.trim().startsWith(enablingControlValue.trim())) {
                return true;
            }
        }

        // Trim both values for a robust comparison, handling potential newline characters from API
        return enablingControlValue?.trim() === (enablingValue || '').trim().replaceAll(String.raw`\n`, '').replaceAll('\n', '');
    }, []);

    const disableLogic = useCallback((attribute: Control, allSectionAttributes: Control[], values: Record<string, string>, entityName?: string): boolean => {
        // Special logic for parent fields in FamilyInformation: disable if student IS independent (selected "Yes")
        if (entityName === ENTITY_NAMES.FAMILY_INFORMATION) {
            const independentAttribute = allSectionAttributes.find(attr =>
                attr.name.toLowerCase().includes('independent student')
            );
            if (independentAttribute) {
                const independentValue = values[independentAttribute._id];
                // If they selected "Yes" (they ARE independent), disable parent fields
                const shouldDisableParentFields = independentValue === 'Yes';

                const parentFieldPatterns = [
                    'parent marital status',
                    'age of oldest parent',
                ];

                if (shouldDisableParentFields && parentFieldPatterns.some(pattern => attribute.name.toLowerCase().includes(pattern))) {
                    return true; // Return true to DISABLE the field
                }
            }
        }

        // A control is disabled if its enabling condition is not met.
        return !evaluateEnablingCondition(attribute, allSectionAttributes, values);
    }, [evaluateEnablingCondition]);

    // Effect to clear ALL parent-related fields when independent student status changes
    useEffect(() => {
        const familyAttributes = tabAttributes[ENTITY_NAMES.FAMILY_INFORMATION] || [];
        const independentAttribute = familyAttributes.find(attr =>
            attr.name.toLowerCase().includes('independent student')
        );
        if (!independentAttribute) return;

        const independentValue = formValues[independentAttribute._id];
        // If user selects "Yes" (they ARE independent), clear parent fields
        const shouldClearParentFields = independentValue === 'Yes';

        if (!shouldClearParentFields) return;

        // Dynamically clear all parent-related fields
        const parentFieldPatterns = [
            'parent marital status',
            'age of oldest parent',
        ];
        const newFormValues = { ...formValues };
        let changed = false;
        for (const attr of familyAttributes) {
            const attrName = attr.name.toLowerCase();
            if (
                parentFieldPatterns.some(pattern => attrName.includes(pattern)) &&
                newFormValues[attr._id] !== undefined
            ) {
                newFormValues[attr._id] = '';
                changed = true;
            }
        }
        if (changed) setFormValues(newFormValues);
    }, [formValues, tabAttributes, setFormValues]);

    const filterControls = (
        attribute: Control,
        allSectionAttributes: Control[],
        values: Record<string, string>,
        entityName?: string
    ): boolean => {
        // Special logic for SAI textbox: don't show if "Help me calculate" is selected
        if (attribute.name === 'Student Aid Index' || attribute.name.toLowerCase().includes('sai')) {
            const aidCalculationAttribute = allSectionAttributes.find(attr =>
                attr.name === "You may choose to skip this section and go directly to a summary of your estimated financial aid package"
            );
            if (aidCalculationAttribute) {
                const selectedOption = values[aidCalculationAttribute._id];
                if (selectedOption === AID_CALCULATION_OPTIONS.NEED_BASED) {
                    return false;
                }
            }
        }

        // If a control has no enabling attribute, it's a primary control and should always be visible.
        if (!attribute.enablingAttribute) {
            return true;
        }

        // For FamilyInformation entity, dependent controls should only be visible if their enabling condition is met
        if (entityName === ENTITY_NAMES.FAMILY_INFORMATION) {
            return evaluateEnablingCondition(attribute, allSectionAttributes, values);
        }

        // For other entities, evaluate the enabling condition.
        // Required fields are NOT automatically shown when they have a non-boolean
        // enabling condition (e.g. "Were you in the top 10%..." only for Alaska).
        const enablingControl = allSectionAttributes.find(
            (attr) => attr.name === attribute.enablingAttribute
        );

        // Only bypass the enabling condition for boolean controllers (checkbox driven visibility).
        if (enablingControl?.dataType === 'Boolean') {
            return true;
        }

        // Required fields must always remain visible (just disabled via disableLogic) so
        // users know they exist — e.g. State stays visible but disabled when Non-USA is selected.
        // Optional conditional fields (e.g. state-specific questions) can be hidden.
        if (attribute.isRequired) {
            return true;
        }

        return evaluateEnablingCondition(attribute, allSectionAttributes, values);
    };

    // Clear values and validation errors of controls that become disabled
    useEffect(() => {
        const controlsToClear: string[] = [];
        for (const attr of allAttributes) {
            // Find which entity this attribute belongs to
            let entityName: string | undefined;
            for (const [name, attrs] of Object.entries(tabAttributes)) {
                if (attrs.some(a => a._id === attr._id)) {
                    entityName = name;
                    break;
                }
            }
            if (disableLogic(attr, allAttributes, formValues, entityName) && formValues[attr._id] !== undefined) {
                controlsToClear.push(attr._id);
            }
        }
        if (controlsToClear.length > 0) {
            setFormValues(prev => {
                const newValues = { ...prev };
                for (const controlId of controlsToClear) {
                    delete newValues[controlId];
                }
                return newValues;
            });

            // Also clear validation errors for disabled controls
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                for (const controlId of controlsToClear) {
                    delete newErrors[controlId];
                }
                return newErrors;
            });
        }
    }, [allAttributes, formValues, disableLogic, setFormValues, tabAttributes]);

    // Example: Use getWelcomeFAQAttributes to render FAQ in the Welcome tab content
    const renderWelcomeFAQ = () => {
        const faqAttributes = getWelcomeFAQAttributes();
        return (
            <div>
                {faqAttributes.map((attr: any) => (
                    <div key={attr._id}>
                        <h3>{attr.name}</h3>
                        <p>{attr.possibleValues}</p>
                    </div>
                ))}
            </div>
        );
    };

    const renderActiveTabContent = () => {
        const entityNames = TAB_ENTITY_MAP[activeTab] || [];
        const allTabAttributes = entityNames.flatMap(entity => tabAttributes[entity] || []);
        const enabledTabAttributes = allTabAttributes.filter(attr => {
            const entityName = entityNames.find(name => {
                const entityAttrs = tabAttributes[name] || [];
                return entityAttrs.some(a => a._id === attr._id);
            });
            // Use allAttributes (all loaded tabs) so cross-tab enabling conditions are resolved correctly.
            return !disableLogic(attr, allAttributes, formValues, entityName);
        });
        // Filter errors for enabled controls only
        const filteredErrors = Object.fromEntries(
            Object.entries(validationErrors).filter(([key]) =>
                enabledTabAttributes.some(attr => attr._id === key)
            )
        );

        // Special handling for Welcome tab to render FAQ
        if (activeTab === 'welcome') {
            return (
                <div className="space-y-6">
                    {renderWelcomeFAQ()}
                </div>
            );
        }

        return (
            <div className="space-y-6 px-8 max-[800px]:px-3">
                {entityNames.map(entityName => {
                    // Find the entity data from the full objects list in derivedAttributesData
                    const entityObject = contextDerivedData?.objects.find(o => o.name === entityName);
                    if (!entityObject) return null;

                    // Create a control-like object for EntityAttributesSection
                    const entityControl = {
                        _id: entityObject.entityId ?? entityObject._id,
                        name: entityObject.name,
                        description: entityObject.description,
                    } as Control;

                    return (
                        <EntityAttributesSection
                            key={entityControl._id}
                            entityControl={entityControl}
                            formValues={formValues}
                            setFormValues={handleValueChange}
                            frmerrors={showErrors ? filteredErrors : {}}
                            authorityId={estimateId ?? undefined}
                            sectionTitle={entityControl.name === ENTITY_NAMES.STUDENT_CONTACT ? 'Student Contact Details' : entityControl.name?.replaceAll(/([a-z])([A-Z])/g, '$1 $2')}
                            sectionDescription={entityControl.description}
                            disableLogic={(attribute: Control) => disableLogic(attribute, allAttributes, formValues, entityName)}
                            filterControls={(attribute: Control) => filterControls(attribute, allAttributes, formValues, entityName)}
                            onAttributesFetched={(attrs: any) => handleAttributesFetched(attrs, entityName)}
                            estimatedNetPrice={entityName === ENTITY_NAMES.SUMMARY ? estimatedNetPrice : undefined}
                            onNavigateToSection={handleNavigateToSection}
                            tabAttributes={tabAttributes}
                            activeTab={activeTab}
                            handleConnectWithUs={handleConnectWithUs}
                            isEditingFromSummary={shouldReturnToSummary}
                            isGlobalValid={isGlobalValid}
                            handleNext={handleNext}
                            loadingNextTab={loadingNextTab}
                            handleBack={handleBack}
                            showErrors={showErrors}
                            calculateDerivedAttributesForEntity={calculateDerivedAttributesForEntity}
                            estimateId={estimateId ?? undefined}
                        />
                    );
                })}
            </div>
        );
    };

    return (
        <div className="w-full">
            <div className="max-w-6xl mx-auto dynamic-tab-container mb-20">
                <div className="relative z-30">
                    <Header lbl={tenantConfig?.authority?.name ?? ''} />
                </div>
                <div className="w-full">
                    <div className="container-fluid mx-auto">
                        <div className="flex flex-col lg:flex-row gap-6 p-4 lg:p-0 pt-20 lg:pt-0 w-full box-border">
                            <div className="w-full lg:flex-1">
                                {/* Sticky Tab Bar */}
                                <div className="top-0 bg-white mt-2 mb-2 relative z-10 dynamic-tab-top" style={{ paddingTop: '100px' }}>
                                    <div className="flex justify-between items-start p-4 relative max-[800px]:overflow-visible max-[800px]:before:content-[''] max-[800px]:before:absolute max-[800px]:before:left-3 max-[800px]:before:right-3 max-[800px]:before:top-1/2 max-[800px]:before:h-0.5 max-[800px]:before:bg-[rgba(153,0,80,0.12)] max-[800px]:before:z-0 max-[800px]:before:rounded-sm max-[800px]:before:pointer-events-none">
                                        {tabs.map((tab, index) => {
                                            const isActive = activeTab === tab;

                                            // Determine skip/disable flags FIRST so completion logic can respect them
                                            const familyAttributes = tabAttributes[ENTITY_NAMES.FAMILY_INFORMATION] || [];
                                            const shouldSkip = shouldSkipToSummary(familyAttributes, formValues);
                                            const isParentFinancesTab = tab === toTabKey(ENTITY_NAMES.PARENT_FINANCES);
                                            const isStudentFinancesTab = tab === toTabKey(ENTITY_NAMES.STUDENT_FINANCES);

                                            // Check if student is independent
                                            const independentAttribute = familyAttributes.find(attr =>
                                                attr.name.toLowerCase().includes('independent student')
                                            );
                                            const studentFinKey = toTabKey(ENTITY_NAMES.STUDENT_FINANCES);


                                            // Detect independent student choice safely and case-insensitively
                                            const independentValue = independentAttribute ? String(formValues[independentAttribute._id] ?? '').toLowerCase() : null;
                                            const isIndependent = independentValue === 'yes';
                                            const isNotIndependent = independentValue === 'no';



                                            // Tab disabled-by-skip logic:
                                            // CRITICAL: When Independent Student is explicitly 'No', NEVER disable finance tabs based on independence
                                            // - Parent: ALWAYS enabled when Independent='No'; disabled when Independent='Yes' OR shouldSkip
                                            // - Student: ALWAYS enabled when Independent='Yes'; ALWAYS enabled when Independent='No'; disabled when shouldSkip only for non-independent
                                            const isTabDisabledBySkip = (() => {
                                                if (isParentFinancesTab) {
                                                    // When Independent Student is 'No', NEVER disable Parent Finances
                                                    if (isNotIndependent) return false;
                                                    // When Independent Student is 'Yes', disable Parent Finances
                                                    if (isIndependent) return true;
                                                    // When user selected SAI or skip options, disable Parent Finances
                                                    if (shouldSkip) return true;
                                                    return false;
                                                }
                                                if (isStudentFinancesTab) {
                                                    // CRITICAL: When Independent Student is 'Yes', NEVER disable Student Finances
                                                    if (isIndependent) return false;
                                                    // When Independent Student is 'No', NEVER disable Student Finances
                                                    if (isNotIndependent) return false;
                                                    // When user selected SAI or skip options (and not independent), disable Student Finances
                                                    if (shouldSkip) return true;
                                                    return false;
                                                }
                                                return false;
                                            })();

                                            // compute current active index to ensure correct highlighting
                                            const currentIndex = tabs.indexOf(activeTab);
                                            const tabIndex = index;
                                            // Treat a tab as completed only if explicitly in completedTabs OR its index is before/equal to current,
                                            // but exclude tabs disabled-by-skip (we don't want skipped tabs to appear "completed" just because index <= current)
                                            const isCompleted = completedTabs.has(tab) || (tabIndex <= currentIndex && !isTabDisabledBySkip);
                                            const Icon = TAB_CONFIG[tab].icon;
                                            const isFilled = isActive || isCompleted;

                                            // Keep Summary tab enabled once it has been reached
                                            const isSummaryTabTarget = tab === toTabKey(ENTITY_NAMES.SUMMARY);
                                            const isFamilyInformationActive = activeTab === toTabKey(ENTITY_NAMES.FAMILY_INFORMATION);
                                            const isParentFinancesActive = activeTab === toTabKey(ENTITY_NAMES.PARENT_FINANCES);

                                            // Enhanced tab navigation logic: allow navigation from Family Information
                                            // EXCEPTIONS: 
                                            // 1. When editing from summary OR on Family Information, allow navigation to any tab except those disabled by skip logic
                                            // 2. When on Parent Finances clicking Student Finances, both should be accessible when independent=No
                                            const isBlockedByTabOrderJsx = isSummaryTabTarget
                                                ? !completedTabs.has(toTabKey(ENTITY_NAMES.SUMMARY))
                                                : index > activeIndex && !completedTabs.has(tabs[index - 1]);

                                            let isTabNavigationDisabled: boolean;
                                            if (shouldReturnToSummary || isFamilyInformationActive) {
                                                // Only skip logic applies when editing from summary or on family info
                                                isTabNavigationDisabled = isTabDisabledBySkip;
                                            } else if (isParentFinancesActive && isStudentFinancesTab && isNotIndependent) {
                                                // CRITICAL: Allow clicking Student Finances from Parent Finances when both are enabled (independent=No)
                                                isTabNavigationDisabled = false;
                                            } else {
                                                isTabNavigationDisabled = isBlockedByTabOrderJsx || isTabDisabledBySkip;
                                            }

                                            // Check if user has visited Summary tab OR if we're past Family Information
                                            const hasVisitedSummary = completedTabs.has(toTabKey(ENTITY_NAMES.SUMMARY));
                                            const hasCompletedFamilyInfo = completedTabs.has(toTabKey(ENTITY_NAMES.FAMILY_INFORMATION));

                                            // Show gray background if tab is disabled by skip AND not active AND either:
                                            // - User has visited Summary OR
                                            // - Tab is Parent Finances, student is independent, and Family Info is completed
                                            // CRITICAL: NEVER show gray background when Independent Student is 'No'
                                            // CRITICAL: Student Finances should NEVER be gray when student is independent (should be enabled/burgundy)
                                            const shouldShowGrayBackground = !isNotIndependent && isTabDisabledBySkip && !isActive && (
                                                hasVisitedSummary ||
                                                (isParentFinancesTab && isIndependent && hasCompletedFamilyInfo)
                                            ) && !(isStudentFinancesTab && isIndependent);

                                            // Set background color based on disabled state FIRST, then active/completed states
                                            const DISABLED_BG = '#D9D9D9';
                                            let iconBackgroundColor: string;
                                            if (shouldShowGrayBackground) {
                                                // Tabs disabled due to skip logic AND user has visited Summary: gray background
                                                iconBackgroundColor = DISABLED_BG;
                                            } else if (isActive) {
                                                iconBackgroundColor = backgroundColor ?? '';
                                            } else if (isCompleted || (tab === 'summary' && completedTabs.has('summary'))) {
                                                iconBackgroundColor = backgroundColor ?? '';
                                            } else {
                                                // Unvisited tabs: white background
                                                iconBackgroundColor = 'white';
                                            }

                                            const iconContainerClass = `w-12 h-12 rounded-full flex items-center justify-center transition-colors z-10 tab-button ${isFilled
                                                ? '' // Filled for active/completed - background will be set via inline style
                                                : 'bg-white border-2' // Border for inactive - border will be set via inline style
                                                } ${isTabNavigationDisabled && !isActive ? 'cursor-not-allowed' : ''}`;

                                            const iconContainerStyle = (isFilled || shouldShowGrayBackground)
                                                ? { backgroundColor: iconBackgroundColor }
                                                : { borderColor: backgroundColor ?? '#095CC9' };

                                            // When a tab should show gray background (disabled by skip AND summary visited), set icon color to dark gray (#4D4D4D)
                                            const activeIconColor = isFilled ? '#fff' : (backgroundColor ?? '#095CC9');
                                            const iconColor = shouldShowGrayBackground
                                                ? '#4D4D4D' // dark gray icon when disabled by skip and summary visited
                                                : activeIconColor;
                                            const completedIconColor = isCompleted ? '#fff' : backgroundColor;
                                            const iconInactiveColor = shouldShowGrayBackground
                                                ? '#4D4D4D'
                                                : completedIconColor;

                                            // remove hardcoded gray so inline style controls the color
                                            // Only use font-medium, font-bold, and text-gray-400 (for disabled) so inline style controls color
                                            const fontWeightClass = isActive ? ' font-bold' : '';
                                            const textColorClass = isTabNavigationDisabled && !isActive ? ' text-gray-900' : '';
                                            const labelClass = `mt-2 text-sm w-24 break-words font-medium${fontWeightClass}${textColorClass} max-[800px]:!hidden max-[800px]:h-0 max-[800px]:p-0 max-[800px]:m-0 max-[800px]:overflow-hidden`;
                                            const labelStyle: React.CSSProperties | undefined = isTabNavigationDisabled && !isActive
                                                ? undefined
                                                : { color: backgroundColor ?? '#095CC9', fontWeight: isActive ? 700 : undefined };

                                            return (
                                                <div key={tab} className="relative flex-1 flex flex-col items-center text-center max-[800px]:z-[2]">
                                                    {/* Connecting line - hidden for the last item */}
                                                    {index < tabs.length - 1 && (
                                                        <div className="absolute top-6 left-1/2 w-full h-0 border border-[#4D4D4D]" style={{ zIndex: 0 }}></div>
                                                    )}

                                                    <button
                                                        type="button"
                                                        onClick={() => handleTabClick(tab, index)}
                                                        className={`flex flex-col items-center focus:outline-none relative ${isActive ? 'max-[800px]:after:content-[\'\'] max-[800px]:after:absolute max-[800px]:after:h-1 max-[800px]:after:w-10 max-[800px]:after:left-1/2 max-[800px]:after:-translate-x-1/2 max-[800px]:after:-bottom-2.5 max-[800px]:after:bg-[var(--tab-accent,#990050)] max-[800px]:after:rounded-sm max-[800px]:after:z-[3]' : ''}`}
                                                        style={{ ['--tab-accent' as any]: iconColor }}
                                                        aria-current={isActive ? 'step' : undefined}
                                                        aria-label={TAB_CONFIG[tab].ariaLabel}
                                                        disabled={isTabNavigationDisabled}
                                                        aria-disabled={isTabNavigationDisabled}
                                                    >
                                                        <div
                                                            className={iconContainerClass}
                                                            style={iconContainerStyle}
                                                        >
                                                            {/* inner wrapper: apply color/fill/stroke so SVGs inherit the intended icon color */}
                                                            <div
                                                                className="rounded-full p-2 transition-colors duration-200"
                                                            >
                                                                <Icon
                                                                    isActive={isActive}
                                                                    isFilled={isFilled}
                                                                    color={iconColor}
                                                                    inactiveColor={iconInactiveColor}
                                                                />
                                                            </div>
                                                        </div >
                                                    </button >
                                                    <span
                                                        className={labelClass}
                                                        style={labelStyle}
                                                    >
                                                        <span>{TAB_CONFIG[tab].ariaLabel}</span>
                                                        {/* If Parent or Student Finances tab is disabled by skip logic, show an asterisk */}
                                                        {(() => {
                                                            // Only show the asterisk for finance tabs when:
                                                            // - the tab is disabled by skip logic AND
                                                            // - the tab is Parent or Student Finances AND
                                                            // - the Summary tab has been visited (user reached Summary)
                                                            // OR when the user explicitly selected Independent='Yes' and has moved to Student Finances
                                                            if (
                                                                (isTabDisabledBySkip && (isParentFinancesTab || isStudentFinancesTab) && hasVisitedSummary)
                                                                || (isParentFinancesTab && isIndependent && activeTab === studentFinKey)
                                                            ) {
                                                                return (
                                                                    <>
                                                                        <span aria-hidden="true" className="ml-1 text-sm text-gray-600">*</span>
                                                                        <span className="sr-only"> (disabled)</span>
                                                                    </>
                                                                );
                                                            }
                                                            return null;
                                                        })()}
                                                    </span>


                                                </div >
                                            );
                                        })}
                                    </div >
                                </div >

                                {/* Scrollable Content Area */}
                                <div className="rounded-lg border border-black mb-20">
                                    <div className="flex-1 overflow-y-auto scrollable-content min-h-0 py-4 border-0">
                                        {renderActiveTabContent()}
                                    </div>

                                    <div className="flex justify-between px-8 py-10 border-t-0">
                                        <div>
                                            {(activeIndex > 0 || activeTab === toTabKey(ENTITY_NAMES.STUDENT_INFORMATION)) && activeTab !== toTabKey(ENTITY_NAMES.SUMMARY) && (
                                                <button
                                                    type="button"
                                                    onClick={handleBack}
                                                    disabled={activeTab === toTabKey(ENTITY_NAMES.STUDENT_INFORMATION)}
                                                    className={`bg-white text-[#AFAFAF] border font-semibold py-2 px-5 rounded hover:border-black hover:text-black hover:underline transition-all duration-200 ${activeTab === toTabKey(ENTITY_NAMES.STUDENT_INFORMATION) ? 'cursor-not-allowed' : ''}`}
                                                    title={activeTab === toTabKey(ENTITY_NAMES.STUDENT_INFORMATION) ? 'Cannot go back from Student Information' : undefined}
                                                    style={{
                                                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.4)'
                                                    }}
                                                >
                                                    Back
                                                </button>
                                            )}
                                        </div>
                                        <div>
                                            {activeTab === 'summary' ? (
                                                <></>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={handleNext}
                                                    className="font-semibold py-2 px-8 rounded transition-all duration-200"
                                                    style={{
                                                        backgroundColor: tenantConfig?.authority?.authorityBranding?.secondaryColor ?? "",
                                                        color: tenantConfig?.authority?.authorityBranding?.accentColor ?? "",
                                                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.6)'
                                                    }}
                                                    disabled={loadingNextTab}
                                                    aria-label={shouldReturnToSummary ? 'Return to summary' : 'Continue to next step'}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.backgroundColor = tenantConfig?.authority?.authorityBranding?.primaryColor ?? '';
                                                        e.currentTarget.style.color = tenantConfig?.authority?.authorityBranding?.accentColor ?? '';
                                                        e.currentTarget.style.textDecoration = 'underline';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.backgroundColor = tenantConfig?.authority?.authorityBranding?.secondaryColor ?? '';
                                                        e.currentTarget.style.color = tenantConfig?.authority?.authorityBranding?.accentColor ?? '';
                                                        e.currentTarget.style.textDecoration = 'none';
                                                    }}
                                                >
                                                    {(() => {
                                                        if (loadingNextTab) return 'Loading...';
                                                        if (shouldReturnToSummary) return 'Return to Summary';
                                                        return 'Continue';
                                                    })()}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div >
                            </div >
                            <div className="w-full py-4 lg:min-w-[400px] lg:max-w-[400px] mt-[50px] lg:mt-[40px] pt-0 lg:pt-[83px]">
                                {(() => {
                                    const showEstimationBlockOnAllPages = tenantConfig?.authority?.authorityBranding?.showEstimationBlockOnAllPages ?? true;
                                    const isSummaryPage = activeTab === toTabKey(ENTITY_NAMES.SUMMARY);

                                    const shouldShowEstimatedPrice = showEstimationBlockOnAllPages || isSummaryPage;

                                    return shouldShowEstimatedPrice ? (
                                        <div className="pb-0 lg:pb-4">
                                            <EstimatedPrice
                                                derivedAttributesData={contextDerivedData}
                                                estimateCalculated={estimateCalculated}
                                                isSummary={activeTab === toTabKey(ENTITY_NAMES.SUMMARY)}
                                                currentTab={activeTab}
                                                disclaimerText={disclaimerText}
                                                noteText={noteText}
                                            />
                                        </div>
                                    ) : null;
                                })()}
                                {faqNameMap[activeTab] && (
                                    <div className="mt-[50px] lg:mt-0">
                                        <WhatYouShouldKnow faqName={faqNameMap[activeTab]} />
                                    </div>
                                )}
                            </div>
                        </div >
                    </div >
                </div >
            </div >
            <Footer isEditingFromSummary={shouldReturnToSummary} onSummaryClick={() => {
                // When footer summary button clicked, navigate back to summary
                setActiveTab(toTabKey(ENTITY_NAMES.SUMMARY));
                setShouldReturnToSummary(false);
            }} />
        </div >
    );
}
